import React, { useState, useCallback, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Container, Row, Col, Card, Button, Badge, Offcanvas, Dropdown, Modal, Spinner, Toast, ToastContainer, Alert, OverlayTrigger, Popover } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Title from '../common/Title';
import { BsDiagram3 } from 'react-icons/bs';
import { FaPlus, FaFileExport, FaObjectGroup, FaObjectUngroup, FaCode, FaSave, FaTimes, FaFileAlt, FaCheckCircle, FaHandPaper, FaMousePointer, FaChevronLeft, FaChevronRight, FaSearch, FaCodeBranch, FaAlignLeft } from 'react-icons/fa';
import { MdGppGood, MdGppBad } from 'react-icons/md';
import StepPalette from './StepPalette';
import { formatRunbook } from '../../utils/runbookLayout';
import EdgeLegend from './EdgeLegend';
import StepTypePickerModal from './StepTypePickerModal';
import StepNode from './nodes/StepNode';
import GroupNode from './nodes/GroupNode';
import NoteNode from './nodes/NoteNode';
import { WorkflowNodesContext } from './nodes/WorkflowNodesContext';
import DraggableEdge from './edges/DraggableEdge';
import { WorkflowEdgesContext } from './edges/WorkflowEdgesContext';
import { findInsertionIndex } from './edges/edgeGeometry';
import StepConfigPanel from './StepConfigPanel';
import { NodeDisplayContext } from './nodes/NodeDisplayContext';
import { buildConnectorColorMap, colorForNode, BRAND_PURPLE } from './nodes/connectorColor';
import ConnectorModal from './ConnectorModal';
import VariableModal from './VariableModal';
import ImportModal from './ImportModal';
import PathSelectionModal from './PathSelectionModal';
import KbBuilderBubble from './aibuilder/KbBuilderBubble';
import config from '../../environments/config';
import { clientConnectorActions } from '../../actions/clientConnector.actions';
import { clientVariableActions } from '../../actions/clientVariable.actions';
import { runbookActions } from '../../actions/runbook.actions';
import { runbookVersionService } from '../../services/runbookVersion.service';
import { runbookService } from '../../services/runbook.service';
import { workflowTemplates, getCategories } from '../../templates/workflows';
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";
import './WorkflowBuilder.css';
import { computeParentStepMap } from './loopParenting';

// Define nodeTypes outside component to maintain stable reference
const nodeTypes = {
  stepNode: StepNode,
  groupNode: GroupNode,
  noteNode: NoteNode,
};

// Registering under 'default' applies it to every edge regardless of whether that edge sets
// its own `type` — none of the many edge-creation call sites in this file do, so this is the
// single place that switches every connection from React Flow's default bezier curve to a
// straight-line path the user can manually bend (see DraggableEdge).
const edgeTypes = {
  default: DraggableEdge,
};

const initialNodes = [
  {
    id: 'start',
    type: 'stepNode',
    position: { x: 250, y: 50 },
    data: {
      label: 'Start',
      stepType: 'start',
      description: 'Workflow Start',
    },
  },
];

const initialEdges = [];

// Shared by openApplyModal's "nothing to apply" short-circuit and the unsaved-changes guard
// on window close: compare CANONICALLY (recursively sorted keys, no whitespace) so key-order
// or formatting differences between the stored version JSON and freshly generated canvas
// JSON aren't seen as edits.
function canonicalizeWorkflowJson(input) {
  try {
    const sort = (v) =>
      Array.isArray(v)
        ? v.map(sort)
        : v && typeof v === 'object'
          ? Object.keys(v).sort().reduce((o, k) => { o[k] = sort(v[k]); return o; }, {})
          : v;
    return JSON.stringify(sort(typeof input === 'string' ? JSON.parse(input) : input));
  } catch {
    return typeof input === 'string' ? input : JSON.stringify(input);
  }
}

// #2102: is nodeId reachable from an error edge (directly, or via a chain of "next" edges)?
// Drives continueAfterError automatically -- no checkbox anywhere.
function isPartOfErrorBranch(nodeId, edges, visited = new Set()) {
  if (visited.has(nodeId)) return false; // cycle guard
  visited.add(nodeId);
  return edges.some((e) => {
    if (e.target !== nodeId) return false;
    if (e.sourceHandle === 'error') return true;
    if (e.sourceHandle === 'next') return isPartOfErrorBranch(e.source, edges, visited);
    return false;
  });
}

// #2662: does this edge's target end a loop? Any edge -- next, true, false -- so it reads as "this
// line goes to End Loop" wherever it occurs on the canvas, which is the whole point on a long
// sweeping line far from its destination. Overrides `stroke` to Armanino purple (the same one
// used for the ifElse step's accent), unmistakable regardless of whether the edge would otherwise
// be a plain next line or a colored True/False branch. Solid, not dashed/dotted -- that was tried
// and dropped in favor of just the color.
const END_LOOP_PURPLE_STYLE = { stroke: BRAND_PURPLE };
function endLoopEdgeStyle(targetId, nodes, baseStyle) {
  const targetNode = nodes.find((n) => n.id === targetId);
  return targetNode?.data?.stepType === 'endLoop' ? { ...baseStyle, ...END_LOOP_PURPLE_STYLE } : baseStyle;
}

const WorkflowBuilder = forwardRef(function WorkflowBuilder(props, ref) {
  const { hideTitle, onDone } = props;
  // Standalone page: sized against the viewport, matching the fixed header above it (the
  // original design — kept as-is, not touched here). Embedded (hideTitle, inside a
  // DesktopWindow): a CSS flex chain (row -> canvas column -> Card -> Card.Body) relies on
  // every intermediate layer correctly resolving flex-grow/percentage height, which proved
  // unreliable in practice — the canvas ended up shorter than the actual window, leaving a
  // blank gap. Measuring the real available space directly (window body's bottom edge minus
  // the row's own top edge) and applying it as an explicit pixel height sidesteps that chain
  // entirely, so it tracks the window's actual current size regardless of which flex layer
  // above it does or doesn't cooperate.
  const canvasRowRef = useRef(null);
  const [measuredCanvasHeight, setMeasuredCanvasHeight] = useState(null);

  useEffect(() => {
    if (!hideTitle) return undefined;
    const rowEl = canvasRowRef.current;
    const bodyEl = rowEl?.closest('.desktop-window-body');
    if (!rowEl || !bodyEl) return undefined;

    const recompute = () => {
      const available = bodyEl.getBoundingClientRect().bottom - rowEl.getBoundingClientRect().top;
      if (available > 0) setMeasuredCanvasHeight(available);
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [hideTitle]);

  const canvasHeight = hideTitle
    ? (measuredCanvasHeight ? `${measuredCanvasHeight}px` : '100%')
    : 'calc(100vh - 280px)';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientConnectorReducer = useSelector((state) => state.clientConnector);
  const clientVariableReducer = useSelector((state) => state.clientVariable);
  const runbookReducer = useSelector((state) => state.runbook);
  const { activeTour, endTour } = useTour();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [stepCounter, setStepCounter] = useState(1);
  const [runbookConnectors, setRunbookConnectors] = useState([]);
  const [runbookVariables, setRunbookVariables] = useState([]);
  const [showAddConnectorModal, setShowAddConnectorModal] = useState(false);
  const [showAddVariableModal, setShowAddVariableModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [aiCanvasFlash, setAiCanvasFlash] = useState(false);
  const aiFlashTimerRef = useRef(null);

  const triggerCanvasFlash = useCallback(() => {
    if (aiFlashTimerRef.current) clearTimeout(aiFlashTimerRef.current);
    setAiCanvasFlash(false);
    requestAnimationFrame(() => {
      setAiCanvasFlash(true);
      aiFlashTimerRef.current = setTimeout(() => setAiCanvasFlash(false), 900);
    });
  }, []);

  useEffect(() => () => {
    if (aiFlashTimerRef.current) clearTimeout(aiFlashTimerRef.current);
  }, []);
  const [groupCounter, setGroupCounter] = useState(1);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [pendingNodeConfig, setPendingNodeConfig] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  // #1977 (Apply model): description prompt shown on Apply / Apply & Close.
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyDescription, setApplyDescription] = useState('');
  const [pendingApplyClose, setPendingApplyClose] = useState(false);
  // Canvas JSON as of the last Apply (or the opened version). Used to skip Apply when nothing changed.
  const [lastAppliedJson, setLastAppliedJson] = useState(null);
  // Close-guard's own dirty-check baseline (separate from lastAppliedJson above — see the
  // designerBaselineRef effect near hasUnsavedChanges for why).
  const [designerHydrated, setDesignerHydrated] = useState(false);
  const designerBaselineRef = useRef(null);
  // #1977: non-blocking "applied but not valid" heads-up (Apply never blocks — invalid just won't run).
  const [showApplyInvalidModal, setShowApplyInvalidModal] = useState(false);
  const [applyInvalidMessage, setApplyInvalidMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPathSelectionModal, setShowPathSelectionModal] = useState(false);
  const [pendingStepContext, setPendingStepContext] = useState(null);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [availableRunbooks, setAvailableRunbooks] = useState([]);
  const [showImportStrippedNotice, setShowImportStrippedNotice] = useState(false);
  const [dragMode, setDragMode] = useState('pan'); // 'pan' or 'select'

  // "Add Step Before"/"Add Step After" from a step's right-click menu: nodeId is the step that
  // was clicked, mode decides which side the new step lands on. Opens StepTypePickerModal; the
  // actual insertion happens in handleInsertStepTypeSelected once a step type is chosen.
  const [insertStepPicker, setInsertStepPicker] = useState(null); // { nodeId, mode: 'before' | 'after' } | null
  // "Add Step After" reuses addNewNode's existing mid-chain-splice/ifElse-path-choice/forEach
  // pairing logic (it already does exactly this, keyed off `selectedNode`) rather than
  // duplicating it — this holds the chosen step type until `selectedNode` has actually caught up
  // to the clicked node (state updates aren't visible synchronously), then the effect below fires.
  const [pendingAfterInsert, setPendingAfterInsert] = useState(null);

  // Shared right-click context menu overlay — multi-select align/indent, edge actions
  // (add/delete a waypoint, delete the line), and DraggableEdge's own "Delete Waypoint" all open
  // the same menu via `openMenu(event, title, items)`, so there's one styled popup (with a
  // title describing what the menu applies to) and one outside-click/Escape dismissal, instead
  // of three near-identical ones. Position is raw viewport coordinates (event.clientX/Y) since
  // the menu is a plain fixed-position overlay, not part of the canvas — it doesn't need to
  // track pan/zoom.
  const [contextMenu, setContextMenu] = useState(null); // { x, y, title, items: [{key,label,onClick}] } | null

  const openMenu = useCallback((event, title, items) => {
    setContextMenu({ x: event.clientX, y: event.clientY, title, items });
  }, []);

  const closeMenu = useCallback(() => setContextMenu(null), []);

  // Aligns the given nodes into an evenly-spaced vertical column: same X (the average of the
  // selection, rather than "the first one" — selection order isn't something the user controls
  // or can see), Y redistributed with consistent spacing. Redistributing Y too (not just
  // unifying X) matters because the nodes are typically a horizontal row to begin with — leaving
  // Y untouched would leave them all sharing their original Y as well, stacked exactly on top of
  // each other instead of forming a column. Sorted by current Y so top-to-bottom order survives.
  const alignSelectionVertically = useCallback(
    (nodeIds) => {
      const VERTICAL_SPACING = 84; // ~60px node + ~24px gap (matches stepSpacing elsewhere)
      setNodes((nds) => {
        const selected = nds.filter((n) => nodeIds.includes(n.id));
        if (selected.length < 2) return nds;

        const averageX = selected.reduce((sum, n) => sum + n.position.x, 0) / selected.length;
        const sortedByY = [...selected].sort((a, b) => a.position.y - b.position.y);
        const startY = sortedByY[0].position.y;
        const yById = new Map(sortedByY.map((n, i) => [n.id, startY + i * VERTICAL_SPACING]));

        return nds.map((n) =>
          nodeIds.includes(n.id) ? { ...n, position: { x: averageX, y: yById.get(n.id) } } : n
        );
      });
      setContextMenu(null);
    },
    [setNodes]
  );

  // Shifts the given nodes' X by a fixed step (same 48px used for nested forEach/if-else blocks
  // elsewhere in this file) — a nudge, not a reformat, so unlike alignSelectionVertically this
  // doesn't touch Y or redistribute anything; each node just moves left/right by the same amount.
  const INDENT_STEP = 48; // matches indentPerLevel elsewhere
  const indentSelection = useCallback(
    (nodeIds, direction) => {
      setNodes((nds) =>
        nds.map((n) =>
          nodeIds.includes(n.id)
            ? { ...n, position: { ...n.position, x: n.position.x + direction * INDENT_STEP } }
            : n
        )
      );
      setContextMenu(null);
    },
    [setNodes]
  );

  const onSelectionContextMenu = useCallback(
    (event, selectedNodes) => {
      event.preventDefault();
      if (selectedNodes.length < 2) {
        setContextMenu(null);
        return;
      }
      const nodeIds = selectedNodes.map((n) => n.id);
      openMenu(event, `${nodeIds.length} Blocks Selected`, [
        { key: 'align-vertical', label: 'Align Vertically', onClick: () => alignSelectionVertically(nodeIds) },
        { key: 'indent', label: 'Indent', onClick: () => indentSelection(nodeIds, 1) },
        { key: 'outdent', label: 'Outdent', onClick: () => indentSelection(nodeIds, -1) },
      ]);
    },
    [openMenu, alignSelectionVertically, indentSelection]
  );

  // Matches this node's X to a neighbor's (the align target is resolved by the caller — see
  // onNodeContextMenu) — a single-node analog of alignSelectionVertically, but a plain nudge
  // rather than a reformat: only this node moves, and only X changes.
  const alignNodeWithNeighbor = useCallback(
    (nodeId, neighborId) => {
      setNodes((nds) => {
        const neighbor = nds.find((n) => n.id === neighborId);
        if (!neighbor) return nds;
        return nds.map((n) =>
          n.id === nodeId ? { ...n, position: { ...n.position, x: neighbor.position.x } } : n
        );
      });
      setContextMenu(null);
    },
    [setNodes]
  );

  // Routes through React Flow's own deleteElements rather than a direct setNodes filter — it
  // resolves connected edges and runs them through onEdgesChange (the error/true/false path
  // cleanup) and the node itself through onNodesDelete (the auto-reconnect bridging), the exact
  // same path pressing the Delete key already takes.
  const deleteNode = useCallback((nodeId) => {
    reactFlowInstance.current?.deleteElements({ nodes: [{ id: nodeId }] });
    setContextMenu(null);
  }, []);

  const onNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();

      // Notes are a free-floating annotation, not a step — no previous/next to align with,
      // just delete.
      if (node.type === 'noteNode') {
        openMenu(event, 'Note', [{ key: 'delete-note', label: 'Delete', onClick: () => deleteNode(node.id) }]);
        return;
      }

      // Scoped to regular step blocks — group nodes aren't part of this (no align/delete
      // semantics defined for a group container).
      if (node.type !== 'stepNode') {
        setContextMenu(null);
        return;
      }

      // The start node can't be deleted, realigned, or have a step added before it (nothing
      // precedes it) — but "Add Step After" is exactly how you insert the very first real step.
      const isStart = node.data?.stepType === 'start';

      const incomingEdge = edges.find((e) => e.target === node.id);
      const outgoingNextEdge = edges.find((e) => e.source === node.id && e.sourceHandle === 'next');
      // An if/else node routes out via 'true'/'false', never 'next' — so outgoingNextEdge simply
      // won't exist for one, which is exactly when "Align with Next" shouldn't be offered (it
      // has two children, not one unambiguous "next" to align with). Symmetrically, a node whose
      // own incoming edge IS a 'true'/'false' branch (i.e., it sits right after an if/else) has
      // no "Align with Previous": that branch is meant to diverge from the if/else, not snap
      // back into its column.
      const canAlignWithPrevious =
        !isStart && !!incomingEdge && incomingEdge.sourceHandle !== 'true' && incomingEdge.sourceHandle !== 'false';
      const canAlignWithNext = !isStart && !!outgoingNextEdge;

      const items = [];
      if (canAlignWithPrevious) {
        items.push({
          key: 'align-previous',
          label: 'Align with Previous',
          onClick: () => alignNodeWithNeighbor(node.id, incomingEdge.source),
        });
      }
      if (canAlignWithNext) {
        items.push({
          key: 'align-next',
          label: 'Align with Next',
          onClick: () => alignNodeWithNeighbor(node.id, outgoingNextEdge.target),
        });
      }
      if (!isStart) {
        items.push({
          key: 'add-step-before',
          label: 'Add Step Before',
          onClick: () => {
            setContextMenu(null);
            setInsertStepPicker({ nodeId: node.id, mode: 'before' });
          },
        });
      }
      items.push({
        key: 'add-step-after',
        label: 'Add Step After',
        onClick: () => {
          setContextMenu(null);
          setInsertStepPicker({ nodeId: node.id, mode: 'after' });
        },
      });
      if (!isStart) {
        items.push({ key: 'delete-step', label: 'Delete', onClick: () => deleteNode(node.id) });
      }

      openMenu(event, node.data?.label || 'Step', items);
    },
    [edges, openMenu, alignNodeWithNeighbor, deleteNode]
  );

  const addNote = useCallback(
    (position) => {
      const id = `note-${Date.now()}`;
      setNodes((nds) => [
        ...nds,
        { id, type: 'noteNode', position, data: { text: '' }, style: { width: 200, height: 120 } },
      ]);
      setContextMenu(null);
    },
    [setNodes]
  );

  const onPaneContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance.current) return;
      const position = reactFlowInstance.current.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      openMenu(event, 'Canvas', [{ key: 'add-note', label: 'Add Note', onClick: () => addNote(position) }]);
    },
    [openMenu, addNote]
  );

  useEffect(() => {
    if (!contextMenu) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [contextMenu, closeMenu]);

  // Distinct color per connector, keyed off the runbook's connector list so two
  // connectors that share a system type still get different colors.
  const connectorColors = useMemo(
    () => buildConnectorColorMap(runbookConnectors),
    [runbookConnectors]
  );
  // Connector details (system + description) keyed by both connectorId and
  // connectorNumber, so a node can build a rich hover tooltip for its CC pill.
  const connectorInfo = useMemo(() => {
    const map = {};
    (runbookConnectors || []).forEach((c) => {
      if (!c) return;
      const info = { connectorNumber: c.connectorNumber, system: c.system, description: c.description };
      if (c.connectorId) map[c.connectorId] = info;
      if (c.connectorNumber) map[c.connectorNumber] = info;
    });
    return map;
  }, [runbookConnectors]);
  const nodeDisplayValue = useMemo(
    () => ({ connectorColors, connectorInfo }),
    [connectorColors, connectorInfo]
  );
  // Memoized so DraggableEdge's context consumption doesn't re-render every edge on every
  // WorkflowBuilder render — setEdges and openMenu are themselves stable, so this only changes
  // when openMenu's own deps change.
  const edgesContextValue = useMemo(() => ({ setEdges, openMenu }), [setEdges, openMenu]);
  // setNodes (from useNodesState) is already referentially stable, so no memoization needed —
  // carried via context (not node.data) for the same reason as WorkflowEdgesContext: nodeTypes
  // must stay a referentially-stable object defined outside the component.
  // Snap-to-grid: when on, dragged nodes align to a fixed grid. Matches the
  // canvas Background dot gap so nodes snap onto the dots. Remembered per browser.
  const SNAP_GRID_SIZE = 12;
  const [snapToGrid, setSnapToGrid] = useState(
    () => localStorage.getItem('wf_snap_grid') === 'true'
  );
  const toggleSnapToGrid = useCallback(() => {
    setSnapToGrid((prev) => {
      const next = !prev;
      localStorage.setItem('wf_snap_grid', String(next));
      return next;
    });
  }, []);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [stepSearch, setStepSearch] = useState('');
  const [trayWidth, setTrayWidth] = useState(380);
  const trayWidthRef = useRef(380);
  const trayDragCleanup = useRef(null);
  const reactFlowInstance = useRef(null);

  useEffect(() => {
    trayWidthRef.current = trayWidth;
  }, [trayWidth]);

  useEffect(() => {
    return () => { if (trayDragCleanup.current) trayDragCleanup.current(); };
  }, []);

  const handleTrayResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = trayWidthRef.current;

    const onMouseMove = (moveEvent) => {
      const newWidth = Math.min(1000, Math.max(280, startWidth + (startX - moveEvent.clientX)));
      setTrayWidth(newWidth);
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      trayDragCleanup.current = null;
    };

    trayDragCleanup.current = cleanup;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
  }, []);

  useEffect(() => {
    if (activeTour === "workflow") {
      setRunTour(true);
    }
  }, [activeTour]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to the Workflow Builder! This is where you can visually design and build complex automation workflows.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="workflow-title"]',
      content: "The Workflow Builder allows you to create runbooks by connecting steps together visually.",
    },
    {
      target: '[data-tour="connectors-bar"]',
      content: "At the top, you can see which connectors and variables are included in this workflow. These are shared resources you can reference across all steps.",
    },
    {
      target: '[data-tour="add-resource-btn"]',
      content: "Add connectors (connections to external systems like Salesforce, databases, etc.) or variables (to store and reuse data across steps via lookup functions) here.",
    },
    {
      target: '[data-tour="step-palette"]',
      content: "This palette contains all available step types. Click any step to add it to your workflow canvas.",
    },
    {
      target: '[data-tour="workflow-canvas"]',
      content: "This is your workflow canvas. Drag steps around to organize them, and connect them by dragging from one step's handle to another.",
    },
    {
      target: '[data-tour="workflow-tools-btn"]',
      content: "Import a workflow from JSON, export or preview yours as JSON, and auto-format the layout — grouped here.",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("workflow");
    }
  };

  // Fetch all available connectors and variables on mount for selection
  useEffect(() => {
    if (props.user && props.user.clientId) {
      dispatch(clientConnectorActions.get({
        pageNumber: 1,
        pageSize: 1000,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid
      }));
      dispatch(clientVariableActions.get({
        pageNumber: 1,
        pageSize: 1000,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid
      }));

      // Fetch available runbooks for ExecuteRunbook step
      runbookService.get({
        pageNumber: 1,
        pageSize: 1000,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid
      }).then(response => {
        setAvailableRunbooks(response.data?.data || []);
      }).catch(() => {
        setAvailableRunbooks([]);
      });
    }
  }, [dispatch, props.user]);

  // Dynamically calculate and update stepOrder and parentStepId whenever nodes or edges change
  useEffect(() => {
    // Which forEach loop each step belongs to (see loopParenting.js)
    const parentStepMap = computeParentStepMap(nodes, edges);

    // Topological sort to determine step order
    const sortedSteps = [];
    const visited = new Set();
    const temp = new Set();

    const visit = (nodeId) => {
      if (temp.has(nodeId)) return;
      if (visited.has(nodeId)) return;

      temp.add(nodeId);

      const incomingEdges = edges.filter(e => e.target === nodeId);
      incomingEdges.forEach(edge => {
        visit(edge.source);
      });

      temp.delete(nodeId);
      visited.add(nodeId);

      const node = nodes.find(n => n.id === nodeId);
      if (node && node.id !== 'start' && node.type !== 'groupNode' && node.type !== 'noteNode') {
        sortedSteps.push(node);
      }
    };

    // Seed the sort from whatever 'start' is actually connected to, so reconnecting it to a
    // different step reliably makes THAT step first (stepOrder 10) — otherwise the fallback
    // loop below visits nodes in array/creation order, unrelated to the graph's actual wiring,
    // and whichever node happens to come first there keeps being treated as first.
    const startEdge = edges.find(e => e.source === 'start');
    if (startEdge) visit(startEdge.target);

    nodes.forEach(node => {
      if (node.id !== 'start' && node.type !== 'groupNode' && node.type !== 'noteNode') {
        visit(node.id);
      }
    });

    // Update nodes with calculated stepOrder and parentStepId
    let needsUpdate = false;
    const updatedNodes = nodes.map(node => {
      if (node.id === 'start' || node.type === 'groupNode' || node.type === 'noteNode') {
        return node;
      }

      const index = sortedSteps.findIndex(n => n.id === node.id);
      const newStepOrder = index >= 0 ? (index + 1) * 10 : undefined;
      const calculatedParentStepId = parentStepMap.get(node.id) || '';

      // Preserve existing parentStepId if:
      // 1. Node already has a parentStepId (was imported or previously set)
      // 2. Calculation returned blank (meaning path to forEach might be broken, e.g., by If/Else)
      // 3. The existing parent forEach still exists in the workflow
      let newParentStepId = calculatedParentStepId;
      if (!calculatedParentStepId && node.data.parentStepId) {
        // Check if the parent forEach still exists
        const parentExists = nodes.some(n => n.id === node.data.parentStepId && n.data.stepType === 'forEach');
        if (parentExists) {
          // Preserve the existing parentStepId
          newParentStepId = node.data.parentStepId;
        }
      }

      if (node.data.stepOrder !== newStepOrder || node.data.parentStepId !== newParentStepId) {
        needsUpdate = true;
        return {
          ...node,
          data: {
            ...node.data,
            stepOrder: newStepOrder,
            parentStepId: newParentStepId,
          },
        };
      }

      return node;
    });

    // Only update if something changed to avoid infinite loops
    if (needsUpdate) {
      setNodes(updatedNodes);
    }
  }, [nodes, edges, setNodes]);

  // #2102: derive continueAfterError from the graph on every change, same pattern as the
  // parentStepId/stepOrder effect above -- covers every place a "next" edge gets created.
  useEffect(() => {
    let needsUpdate = false;
    const updatedNodes = nodes.map((node) => {
      const shouldContinue = isPartOfErrorBranch(node.id, edges);
      if (Boolean(node.data.continueAfterError) !== shouldContinue) {
        needsUpdate = true;
        return { ...node, data: { ...node.data, continueAfterError: shouldContinue } };
      }
      return node;
    });

    if (needsUpdate) {
      setNodes(updatedNodes);
    }
  }, [nodes, edges, setNodes]);

  // Sync edges when conditionTruePath/conditionFalsePath changes on ifElse nodes
  useEffect(() => {
    const ifElseNodes = nodes.filter(n => n.data.stepType === 'ifElse');

    if (ifElseNodes.length === 0) return;

    setEdges((currentEdges) => {
      let edgesNeedUpdate = false;
      const newEdges = [...currentEdges];

      ifElseNodes.forEach(node => {
        const truePath = node.data.conditionTruePath;
        const falsePath = node.data.conditionFalsePath;

        // Check for true edge
        const existingTrueEdge = currentEdges.find(e => e.source === node.id && e.sourceHandle === 'true');

        if (truePath && truePath !== '') {
          if (!existingTrueEdge || existingTrueEdge.target !== truePath) {
            edgesNeedUpdate = true;
            // Remove old true edge if it exists
            const filteredEdges = newEdges.filter(e => !(e.source === node.id && e.sourceHandle === 'true'));
            newEdges.length = 0;
            newEdges.push(...filteredEdges);
            // Add new true edge
            newEdges.push({
              id: `${node.id}-true-${truePath}`,
              source: node.id,
              target: truePath,
              sourceHandle: 'true',
              label: 'True',
              style: endLoopEdgeStyle(truePath, nodes, { stroke: '#10b981' }),
            });
          }
        } else if (existingTrueEdge) {
          // Remove true edge if truePath is empty
          edgesNeedUpdate = true;
          const filteredEdges = newEdges.filter(e => !(e.source === node.id && e.sourceHandle === 'true'));
          newEdges.length = 0;
          newEdges.push(...filteredEdges);
        }

        // Check for false edge
        const existingFalseEdge = currentEdges.find(e => e.source === node.id && e.sourceHandle === 'false');

        if (falsePath && falsePath !== '') {
          if (!existingFalseEdge || existingFalseEdge.target !== falsePath) {
            edgesNeedUpdate = true;
            // Remove old false edge if it exists
            const filteredEdges = newEdges.filter(e => !(e.source === node.id && e.sourceHandle === 'false'));
            newEdges.length = 0;
            newEdges.push(...filteredEdges);
            // Add new false edge
            newEdges.push({
              id: `${node.id}-false-${falsePath}`,
              source: node.id,
              target: falsePath,
              sourceHandle: 'false',
              label: 'False',
              style: endLoopEdgeStyle(falsePath, nodes, { stroke: '#f59e0b' }),
            });
          }
        } else if (existingFalseEdge) {
          // Remove false edge if falsePath is empty
          edgesNeedUpdate = true;
          const filteredEdges = newEdges.filter(e => !(e.source === node.id && e.sourceHandle === 'false'));
          newEdges.length = 0;
          newEdges.push(...filteredEdges);
        }
      });

      return edgesNeedUpdate ? newEdges : currentEdges;
    });
  }, [nodes, setEdges]);

  const onConnect = useCallback(
    (params) => {
      // If connecting from the error handle, set errorStepId on the source node
      if (params.sourceHandle === 'error') {
        setNodes((nds) =>
          nds.map((node) =>
            node.id === params.source
              ? { ...node, data: { ...node.data, errorStepId: params.target } }
              : node
          )
        );
        // Add edge with red dashed styling for error connections
        const errorEdge = {
          ...params,
          id: `${params.source}-error-${params.target}`,
          label: 'Error',
          style: { stroke: '#ef4444', strokeDasharray: '5,5' },
        };
        setEdges((eds) => addEdge(errorEdge, eds));
      } else if (params.sourceHandle === 'true') {
        // If connecting from the true handle of an ifElse node, set conditionTruePath
        // Get the target node's stepId
        const targetNode = nodes.find(n => n.id === params.target);
        const targetStepId = targetNode?.data?.stepId || params.target;
        setNodes((nds) =>
          nds.map((node) =>
            node.id === params.source
              ? { ...node, data: { ...node.data, conditionTruePath: targetStepId } }
              : node
          )
        );
        // The edge will be added by the useEffect that syncs ifElse edges
      } else if (params.sourceHandle === 'false') {
        // If connecting from the false handle of an ifElse node, set conditionFalsePath
        // Get the target node's stepId
        const targetNode = nodes.find(n => n.id === params.target);
        const targetStepId = targetNode?.data?.stepId || params.target;
        setNodes((nds) =>
          nds.map((node) =>
            node.id === params.source
              ? { ...node, data: { ...node.data, conditionFalsePath: targetStepId } }
              : node
          )
        );
        // The edge will be added by the useEffect that syncs ifElse edges
      } else {
        const style = endLoopEdgeStyle(params.target, nodes, params.style);
        const edgeParams = style ? { ...params, style } : params;
        setEdges((eds) => addEdge(edgeParams, eds));
      }
    },
    [setEdges, setNodes, nodes]
  );

  // Custom edge change handler to clear errorStepId/conditionTruePath/conditionFalsePath when edges are removed
  const onEdgesChange = useCallback((changes) => {
    // Check for removed edges
    changes.forEach((change) => {
      if (change.type === 'remove') {
        const removedEdge = edges.find((e) => e.id === change.id);
        if (removedEdge) {
          if (removedEdge.sourceHandle === 'error') {
            // Clear errorStepId on the source node
            setNodes((nds) =>
              nds.map((node) =>
                node.id === removedEdge.source
                  ? { ...node, data: { ...node.data, errorStepId: null } }
                  : node
              )
            );
          } else if (removedEdge.sourceHandle === 'true') {
            // Clear conditionTruePath on the source ifElse node
            setNodes((nds) =>
              nds.map((node) =>
                node.id === removedEdge.source
                  ? { ...node, data: { ...node.data, conditionTruePath: '' } }
                  : node
              )
            );
          } else if (removedEdge.sourceHandle === 'false') {
            // Clear conditionFalsePath on the source ifElse node
            setNodes((nds) =>
              nds.map((node) =>
                node.id === removedEdge.source
                  ? { ...node, data: { ...node.data, conditionFalsePath: '' } }
                  : node
              )
            );
          }
        }
      }
    });
    onEdgesChangeBase(changes);
  }, [edges, setNodes, onEdgesChangeBase]);

  // Removing an edge has to go through onEdgesChange (not a direct setEdges filter) — that's
  // the handler above that clears errorStepId/conditionTruePath/conditionFalsePath on the source
  // node when the edge representing one of those gets removed. Bypassing it would leave a stale
  // path pointer on a node whose branch/error line just got deleted.
  const deleteEdge = useCallback(
    (edgeId) => {
      onEdgesChange([{ type: 'remove', id: edgeId }]);
      setContextMenu(null);
    },
    [onEdgesChange]
  );

  const addWaypointToEdge = useCallback(
    (edge, event) => {
      if (!reactFlowInstance.current) return;
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);
      if (!sourceNode || !targetNode) return;

      // Approximate each node's center (not the exact handle position DraggableEdge has access
      // to as props) — close enough to decide which segment a click landed nearest to.
      const center = (n) => ({
        x: n.position.x + (n.width || 200) / 2,
        y: n.position.y + (n.height || 60) / 2,
      });
      const click = reactFlowInstance.current.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const waypoints = edge.data?.waypoints || [];
      const points = [center(sourceNode), ...waypoints, center(targetNode)];
      const next = [...waypoints];
      next.splice(findInsertionIndex(points, click), 0, click);

      setEdges((eds) => eds.map((e) => (e.id === edge.id ? { ...e, data: { ...e.data, waypoints: next } } : e)));
      setContextMenu(null);
    },
    [nodes, setEdges]
  );

  const onEdgeContextMenu = useCallback(
    (event, edge) => {
      event.preventDefault();
      openMenu(event, 'Line', [
        { key: 'add-waypoint', label: 'Add Waypoint', onClick: () => addWaypointToEdge(edge, event) },
        { key: 'delete-line', label: 'Delete Line', onClick: () => deleteEdge(edge.id) },
      ]);
    },
    [openMenu, addWaypointToEdge, deleteEdge]
  );

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...newData } }
          : node
      )
    );
  }, [setNodes]);

  const onNodeClick = useCallback((event, node) => {
    // Save pending config before switching nodes
    if (selectedNode && pendingNodeConfig) {
      updateNodeData(selectedNode.id, pendingNodeConfig);
    }
    setSelectedNode(node);
  }, [selectedNode, pendingNodeConfig, updateNodeData]);

  const onPaneClick = useCallback(() => {
    // Save pending config before deselecting
    if (selectedNode && pendingNodeConfig) {
      updateNodeData(selectedNode.id, pendingNodeConfig);
    }
    setSelectedNode(null);
    setContextMenu(null);
  }, [selectedNode, pendingNodeConfig, updateNodeData]);

  const onNodesDelete = useCallback((deleted) => {
    setSelectedNode(null);

    // Auto-reconnect: bridge each deleted node's incoming edges to its 'next' successor so the
    // chain doesn't get orphaned. This must handle every incoming handle — not just 'next', but
    // also the if/else 'true'/'false' branches and the 'error' path. For a branch/error, the
    // wiring lives on the SOURCE node's data (conditionTruePath/conditionFalsePath/errorStepId),
    // and the true/false edges are re-derived by the sync effect — so we repoint node data there.
    //
    // Ordering note: ReactFlow's deleteElements fires onEdgesChange (which clears the branch/error
    // path on the source when its edge is removed) BEFORE onNodesDelete, so the writes below are
    // queued last and win.
    deleted.forEach(deletedNode => {
      if (deletedNode.id === 'start' || deletedNode.type === 'groupNode') return;

      // Where the deleted node continued to on the normal path (null if it was the last step).
      const outgoingEdge = edges.find(e => e.source === deletedNode.id && e.sourceHandle === 'next');
      const successorId = outgoingEdge?.target || null;

      // Every edge feeding the deleted node, regardless of handle (next / true / false / error).
      const incomingEdges = edges.filter(e => e.target === deletedNode.id);

      incomingEdges.forEach(inEdge => {
        if (inEdge.sourceHandle === 'true' || inEdge.sourceHandle === 'false') {
          // If/Else branch: repoint the branch to the successor (or clear it if the deleted node
          // was the last step on the branch). The sync effect rebuilds the edge from this value.
          const pathField = inEdge.sourceHandle === 'true' ? 'conditionTruePath' : 'conditionFalsePath';
          setNodes((nds) => nds.map((n) =>
            n.id === inEdge.source ? { ...n, data: { ...n.data, [pathField]: successorId || '' } } : n
          ));
        } else if (inEdge.sourceHandle === 'error') {
          // Error path: repoint errorStepId and rebuild the (directly-managed) error edge.
          setNodes((nds) => nds.map((n) =>
            n.id === inEdge.source ? { ...n, data: { ...n.data, errorStepId: successorId || null } } : n
          ));
          if (successorId) {
            setEdges((eds) => [...eds, {
              id: `${inEdge.source}-error-${successorId}`,
              source: inEdge.source,
              target: successorId,
              sourceHandle: 'error',
              label: 'Error',
              style: { stroke: '#ef4444', strokeDasharray: '5,5' },
            }]);
          }
        } else if (successorId) {
          // Normal 'next' chain: bridge predecessor -> successor.
          const style = endLoopEdgeStyle(successorId, nodes, undefined);
          setEdges((eds) => [...eds, {
            id: `${inEdge.source}-${successorId}`,
            source: inEdge.source,
            target: successorId,
            sourceHandle: 'next',
            ...(style && { style }),
          }]);
        }
      });
    });
  }, [edges, nodes, setNodes, setEdges]);

  // Handle path selection from modal for If/Else nodes
  const handlePathSelect = useCallback((pathType) => {
    if (!pendingStepContext) return;

    const { stepType, stepId, newNode, selectedNode } = pendingStepContext;
    const pathField = pathType === 'true' ? 'conditionTruePath' : 'conditionFalsePath';

    // Apply horizontal offset based on path type
    // True path: offset to the right by 200px (1 step width)
    // False path: offset to the left by 200px (1 step width)
    const horizontalOffset = pathType === 'true' ? 200 : -200;
    const adjustedNode = {
      ...newNode,
      position: {
        x: newNode.position.x + horizontalOffset,
        y: newNode.position.y,
      },
    };

    let newEdges = [];

    // Set the chosen path on the If/Else node to point to the new step
    updateNodeData(selectedNode.id, { [pathField]: stepId });

    // Check BOTH paths to see if either connects to an endLoop
    // We need to check the current state of the nodes, not just selectedNode.data
    const currentIfElseNode = nodes.find(n => n.id === selectedNode.id);
    const truePath = pathType === 'true' ? stepId : (currentIfElseNode?.data.conditionTruePath || selectedNode.data.conditionTruePath);
    const falsePath = pathType === 'false' ? stepId : (currentIfElseNode?.data.conditionFalsePath || selectedNode.data.conditionFalsePath);

    // Check if either path (or what it connects to) is an endLoop
    let endLoopTarget = null;

    // Check true path
    if (truePath && truePath !== stepId) {
      const truePathNode = nodes.find(n => n.id === truePath);
      if (truePathNode) {
        // Check if the true path node itself is an endLoop
        if (truePathNode.data.stepType === 'endLoop') {
          endLoopTarget = truePath;
        } else {
          // Check if the true path node connects to an endLoop via its nextStepId
          const truePathNextEdge = edges.find(e => e.source === truePath && e.sourceHandle === 'next');
          if (truePathNextEdge) {
            const truePathNextNode = nodes.find(n => n.id === truePathNextEdge.target);
            if (truePathNextNode && truePathNextNode.data.stepType === 'endLoop') {
              endLoopTarget = truePathNextEdge.target;
            }
          }
        }
      }
    }

    // Check false path if we haven't found an endLoop yet
    if (!endLoopTarget && falsePath && falsePath !== stepId) {
      const falsePathNode = nodes.find(n => n.id === falsePath);
      if (falsePathNode) {
        // Check if the false path node itself is an endLoop
        if (falsePathNode.data.stepType === 'endLoop') {
          endLoopTarget = falsePath;
        } else {
          // Check if the false path node connects to an endLoop via its nextStepId
          const falsePathNextEdge = edges.find(e => e.source === falsePath && e.sourceHandle === 'next');
          if (falsePathNextEdge) {
            const falsePathNextNode = nodes.find(n => n.id === falsePathNextEdge.target);
            if (falsePathNextNode && falsePathNextNode.data.stepType === 'endLoop') {
              endLoopTarget = falsePathNextEdge.target;
            }
          }
        }
      }
    }

    // If we found an endLoop, connect the new step to it
    if (endLoopTarget) {
      newEdges.push({
        id: `${stepId}-${endLoopTarget}`,
        source: stepId,
        target: endLoopTarget,
        sourceHandle: 'next',
      });
    }

    // Add the adjusted node and edges
    setNodes((nds) => [...nds, adjustedNode]);
    setStepCounter(prev => prev + 1);

    if (newEdges.length > 0) {
      setEdges((eds) => [...eds, ...newEdges]);
    }

    // Auto-select the newly added node
    setSelectedNode(adjustedNode);

    // Clear pending context and close modal
    setPendingStepContext(null);
    setShowPathSelectionModal(false);
  }, [pendingStepContext, nodes, updateNodeData, setNodes, setEdges, setStepCounter, setSelectedNode]);

  const addNewNode = useCallback((stepType) => {
    const timestamp = Date.now();
    const stepId = `RS${stepCounter}`;

    // Spacing constants
    const stepSpacing = 84; // ~60px node + ~24px gap (one row of dots); 7× the 12px grid
    const indentPerLevel = 48; // 4 grid squares (4 × 12px) of nesting indent

    // Calculate nesting depth for intelligent indentation
    const calculateNestingDepth = (node) => {
      if (!node) return 0;

      // Count how many forEach loops this node is inside
      let depth = 0;
      let currentNode = node;

      // Traverse up through parent forEach loops
      while (currentNode) {
        if (currentNode.data && currentNode.data.parentStepId) {
          const parentNode = nodes.find(n => n.id === currentNode.data.parentStepId);
          if (parentNode && parentNode.data.stepType === 'forEach') {
            depth++;
          }
          currentNode = parentNode;
        } else {
          break;
        }
      }

      return depth;
    };

    // Calculate position based on selected node or existing nodes
    let xPosition = 250;
    let yPosition = 150;

    // Calculate base position offset with indentation for nested loops
    const nestingDepth = calculateNestingDepth(selectedNode);

    // If adding a forEach, it will be at the same nesting level as selected node
    // If adding other nodes, they inherit the nesting of selected node
    const baseOffset = nestingDepth * indentPerLevel;

    if (selectedNode) {
      // Check if we're inserting in the middle of a chain
      const existingEdge = edges.find(e => e.source === selectedNode.id && e.sourceHandle === 'next');

      if (existingEdge) {
        // Inserting in middle - place new node between selected and next
        const nextNode = nodes.find(n => n.id === existingEdge.target);
        if (nextNode) {
          // Position after selected node
          xPosition = selectedNode.position.x;
          yPosition = selectedNode.position.y + stepSpacing;

          // Shift all subsequent nodes down to make room
          const nodesToShift = [];
          const visited = new Set();
          const queue = [existingEdge.target];

          while (queue.length > 0) {
            const currentId = queue.shift();
            if (visited.has(currentId)) continue;
            visited.add(currentId);

            const currentNode = nodes.find(n => n.id === currentId);
            if (currentNode && currentNode.type !== 'groupNode') {
              nodesToShift.push(currentNode);
            }

            // Find all outgoing edges
            const outgoing = edges.filter(e => e.source === currentId);
            outgoing.forEach(e => {
              if (!visited.has(e.target)) {
                queue.push(e.target);
              }
            });
          }

          // Shift subsequent nodes down to make room
          if (nodesToShift.length > 0) {
            setNodes((nds) => nds.map(node => {
              if (nodesToShift.find(n => n.id === node.id)) {
                return {
                  ...node,
                  position: {
                    x: node.position.x,
                    y: node.position.y + stepSpacing,
                  },
                };
              }
              return node;
            }));
          }
        }
      } else {
        // Just placing after selected node (no existing connection)
        xPosition = selectedNode.position.x;
        yPosition = selectedNode.position.y + stepSpacing;
      }
    } else if (nodes.length > 0) {
      // Place after the last node
      const maxY = Math.max(...nodes.map(n => n.position.y));
      yPosition = maxY + stepSpacing;
      xPosition = 250 + baseOffset; // Apply indentation even when no node is selected
    }

    // For forEach loops, add indentation to the already calculated position
    const isForEach = stepType.type === 'forEach';
    if (isForEach && selectedNode) {
      // Add forEach indentation to the already-set position
      xPosition += indentPerLevel;
    }

    // Build initial node data
    const nodeData = {
      stepId: stepId,
      label: stepType.label,
      stepType: stepType.type,
      description: stepType.description,
      action: stepType.action,
    };

    const newNode = {
      id: stepId,
      type: 'stepNode',
      position: {
        x: xPosition,
        y: yPosition,
      },
      data: nodeData,
    };

    // Auto-connection logic
    // If/Else steps should never have a "next" edge, only true/false edges
    const isIfElse = stepType.type === 'ifElse';

    let newEdges = [];

    // Check if the SELECTED node is an If/Else (not the new node)
    const selectedNodeIsIfElse = selectedNode && selectedNode.data.stepType === 'ifElse';

    if (selectedNode && selectedNode.id !== 'start') {
      // If the selected node is an If/Else, ask which path to add to
      if (selectedNodeIsIfElse) {
        // Show modal and store context for later completion
        setPendingStepContext({
          stepType,
          stepId,
          newNode,
          selectedNode,
        });
        setShowPathSelectionModal(true);
        return; // Exit early - will resume in modal callback
      } else {
        // Find any existing edge from the selected node
        const existingEdge = edges.find(e => e.source === selectedNode.id && e.sourceHandle === 'next');

        if (existingEdge) {
          // SPECIAL CASE: If inserting an If/Else, handle it first before other logic
          if (isIfElse) {
            // For If/Else: connect both true and false paths to the next step
            // Insert: selectedNode -> ifElse -> nextStep (both true and false to nextStep)
            setEdges((eds) => eds.filter(e => e.id !== existingEdge.id));

            // Set both paths to point to the next step directly on nodeData
            nodeData.conditionTruePath = existingEdge.target;
            nodeData.conditionFalsePath = existingEdge.target;

            newEdges = [
              {
                id: `${selectedNode.id}-${stepId}`,
                source: selectedNode.id,
                target: stepId,
                sourceHandle: 'next',
              }
            ];
          } else {
            // Check if the selected node is a forEach and the target is its matching endLoop
            const targetNode = nodes.find(n => n.id === existingEdge.target);

            // Case 1: Direct forEach -> endLoop
            const isForEachToEndLoop = selectedNode.data.stepType === 'forEach' &&
                                        targetNode &&
                                        targetNode.data.stepType === 'endLoop' &&
                                        (targetNode.data.parentStepId === selectedNode.data.parentStepId ||
                                         (!targetNode.data.parentStepId && !selectedNode.data.parentStepId));

            // Case 2: Step inside forEach -> endLoop (detect by matching parentStepIds OR by traversing backwards)
            let isInsideLoopToEndLoop = false;
            if (targetNode && targetNode.data.stepType === 'endLoop') {
            // First try matching parentStepIds if they exist
            if (selectedNode.data.parentStepId && targetNode.data.parentStepId === selectedNode.data.parentStepId) {
              isInsideLoopToEndLoop = true;
            } else {
              // If parentStepIds aren't set, traverse backwards from endLoop to find matching forEach
              // This handles cases where the workflow was imported and parentStepIds haven't been calculated yet
              const visited = new Set();
              const queue = [targetNode.id];
              let foundForEach = null;

              while (queue.length > 0 && !foundForEach) {
                const currentId = queue.shift();
                if (visited.has(currentId)) continue;
                visited.add(currentId);

                const currentNode = nodes.find(n => n.id === currentId);
                if (currentNode && currentNode.data.stepType === 'forEach') {
                  foundForEach = currentNode;
                  break;
                }

                // Get all incoming edges (traverse backwards)
                const incomingEdges = edges.filter(e => e.target === currentId && e.sourceHandle === 'next');
                incomingEdges.forEach(edge => {
                  if (!visited.has(edge.source)) {
                    queue.push(edge.source);
                  }
                });
              }

              // If we found a forEach, check if selected node is between that forEach and this endLoop
              if (foundForEach) {
                const forwardVisited = new Set();
                const forwardQueue = [foundForEach.id];

                while (forwardQueue.length > 0) {
                  const currentId = forwardQueue.shift();
                  if (forwardVisited.has(currentId)) continue;
                  forwardVisited.add(currentId);

                  if (currentId === selectedNode.id) {
                    isInsideLoopToEndLoop = true;
                    break;
                  }

                  if (currentId === targetNode.id) break; // Don't traverse past the endLoop

                  const outgoingEdges = edges.filter(e => e.source === currentId && e.sourceHandle === 'next');
                  outgoingEdges.forEach(edge => {
                    if (!forwardVisited.has(edge.target)) {
                      forwardQueue.push(edge.target);
                    }
                  });
                }
              }
            }
          }

          if (isForEachToEndLoop || isInsideLoopToEndLoop) {
            // Special case: inserting between forEach and its endLoop
            // Remove the existing edge and create: selectedNode -> newNode -> endLoop
            setEdges((eds) => eds.filter(e => e.id !== existingEdge.id));

            newEdges = [
              {
                id: `${selectedNode.id}-${stepId}`,
                source: selectedNode.id,
                target: stepId,
                sourceHandle: 'next',
              },
              {
                id: `${stepId}-${existingEdge.target}`,
                source: stepId,
                target: existingEdge.target,
                sourceHandle: 'next',
              }
            ];
          } else {
            // Insert in the middle: selectedNode -> newNode -> existingTarget
            // Remove the old edge and create two new ones
            setEdges((eds) => eds.filter(e => e.id !== existingEdge.id));
            newEdges = [
              {
                id: `${selectedNode.id}-${stepId}`,
                source: selectedNode.id,
                target: stepId,
                sourceHandle: 'next',
              },
              {
                id: `${stepId}-${existingEdge.target}`,
                source: stepId,
                target: existingEdge.target,
                sourceHandle: 'next',
              }
            ];
          }
          }
        } else {
          // No existing edge - just connect from selected node to new node
            newEdges = [{
              id: `${selectedNode.id}-${stepId}`,
              source: selectedNode.id,
              target: stepId,
              sourceHandle: 'next',
            }];
          }
      }
    } else if (selectedNode && selectedNode.id === 'start') {
      // Connect from start node
      const existingEdge = edges.find(e => e.source === 'start');
      if (existingEdge) {
        if (isIfElse) {
          // For If/Else: only connect TO it, don't connect FROM it
          setEdges((eds) => eds.filter(e => e.source !== 'start'));
          newEdges = [
            {
              id: `start-${stepId}`,
              source: 'start',
              target: stepId,
              sourceHandle: 'next',
            }
          ];
          // Note: existingEdge.target is orphaned, user must manually reconnect
        } else {
          // Insert after start
          setEdges((eds) => eds.filter(e => e.source !== 'start'));
          newEdges = [
            {
              id: `start-${stepId}`,
              source: 'start',
              target: stepId,
              sourceHandle: 'next',
            },
            {
              id: `${stepId}-${existingEdge.target}`,
              source: stepId,
              target: existingEdge.target,
              sourceHandle: 'next',
            }
          ];
        }
      } else {
        newEdges = [{
          id: `start-${stepId}`,
          source: 'start',
          target: stepId,
          sourceHandle: 'next',
        }];
      }
    }

    // If adding a forEach loop, also add an endLoop node
    if (stepType.type === 'forEach') {
      const endLoopStepId = `RS${stepCounter + 1}`;

      // Calculate endLoop position - should align with the forEach itself (not indented further)
      // The forEach is already indented if it's nested, so endLoop matches its X position
      const endLoopNode = {
        id: endLoopStepId,
        type: 'stepNode',
        position: {
          x: newNode.position.x - indentPerLevel, // Unindent back to the parent level
          y: newNode.position.y + 150,
        },
        data: {
          stepId: endLoopStepId,
          label: 'End Loop',
          stepType: 'endLoop',
          description: 'End loop iteration',
          action: 17,
        },
      };

      // Find the last edge in newEdges that connects TO something (the continuation after forEach)
      // This would be the edge like: forEach -> nextNode
      const continuationEdge = newEdges.find(e => e.source === stepId);

      if (continuationEdge) {
        // Insert endLoop between forEach and the next node
        // Change: forEach -> nextNode  TO:  forEach -> endLoop -> nextNode
        const targetId = continuationEdge.target;

        // Remove the direct forEach -> nextNode edge
        newEdges = newEdges.filter(e => e.id !== continuationEdge.id);

        // Add forEach -> endLoop
        newEdges.push({
          id: `${stepId}-${endLoopStepId}`,
          source: stepId,
          target: endLoopStepId,
          sourceHandle: 'next',
        });

        // Add endLoop -> nextNode
        newEdges.push({
          id: `${endLoopStepId}-${targetId}`,
          source: endLoopStepId,
          target: targetId,
          sourceHandle: 'next',
        });
      } else {
        // No continuation, just connect forEach to endLoop
        newEdges.push({
          id: `${stepId}-${endLoopStepId}`,
          source: stepId,
          target: endLoopStepId,
          sourceHandle: 'next',
        });
      }

      setNodes((nds) => [...nds, newNode, endLoopNode]);
      setStepCounter(prev => prev + 2);
    } else {
      setNodes((nds) => [...nds, newNode]);
      setStepCounter(prev => prev + 1);
    }

    // Add the new edges
    if (newEdges.length > 0) {
      setEdges((eds) => [...eds, ...newEdges]);
    }

    // Auto-select the newly added node so you can keep clicking to add more
    setSelectedNode(newNode);
  }, [setNodes, setEdges, stepCounter, nodes, edges, selectedNode]);

  // Completes an "Add Step After" started from a step's right-click menu (see
  // handleInsertStepTypeSelected below): that handler selects the clicked node and stashes the
  // chosen step type here, then this effect fires once `selectedNode` has actually caught up
  // (state updates aren't visible synchronously) and hands off to addNewNode exactly as if the
  // user had clicked that step type in the palette while the node was already selected — so it
  // gets the same mid-chain splice / ifElse path-choice modal / forEach+endLoop pairing for free.
  useEffect(() => {
    if (pendingAfterInsert && selectedNode) {
      addNewNode(pendingAfterInsert);
      setPendingAfterInsert(null);
    }
  }, [selectedNode, pendingAfterInsert, addNewNode]);

  // Inserts a new step directly before `targetNode`, rewiring whatever currently feeds into it
  // (preserving each incoming edge's handle — next/true/false/error — so branch/error semantics
  // on the upstream node survive) to feed into the new step instead, then chains the new step
  // into targetNode. There's no "selectedNode"-based precedent for this direction (addNewNode
  // only ever inserts after), so this is a standalone counterpart rather than a reuse.
  const insertStepBefore = useCallback(
    (targetNode, stepType) => {
      const stepSpacing = 84; // matches addNewNode's spacing
      const isForEach = stepType.type === 'forEach';
      const isIfElse = stepType.type === 'ifElse';
      const stepId = `RS${stepCounter}`;

      const incomingEdges = edges.filter((e) => e.target === targetNode.id);

      // Shift targetNode and everything downstream of it to make room above it. A forEach also
      // brings its paired endLoop, so it needs twice the room.
      const shiftAmount = isForEach ? stepSpacing * 2 : stepSpacing;
      const nodesToShift = [];
      const visited = new Set();
      const queue = [targetNode.id];
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const currentNode = nodes.find((n) => n.id === currentId);
        if (currentNode && currentNode.type !== 'groupNode') {
          nodesToShift.push(currentNode);
        }
        edges
          .filter((e) => e.source === currentId)
          .forEach((e) => {
            if (!visited.has(e.target)) queue.push(e.target);
          });
      }

      const nodeData = {
        stepId,
        label: stepType.label,
        stepType: stepType.type,
        description: stepType.description,
        action: stepType.action,
      };
      if (isIfElse) {
        // Both branches continue on to whatever used to come right before targetNode
        nodeData.conditionTruePath = targetNode.id;
        nodeData.conditionFalsePath = targetNode.id;
      }

      const newNode = {
        id: stepId,
        type: 'stepNode',
        position: { x: targetNode.position.x, y: targetNode.position.y },
        data: nodeData,
      };

      // Reroute each edge that currently targets targetNode to target the new node instead,
      // preserving its handle so true/false/error semantics on the SOURCE node stay correct.
      const sourceDataPatches = new Map(); // sourceNodeId -> partial data patch
      const replacementEdges = [];
      incomingEdges.forEach((edge) => {
        if (edge.sourceHandle === 'true' || edge.sourceHandle === 'false') {
          const field = edge.sourceHandle === 'true' ? 'conditionTruePath' : 'conditionFalsePath';
          sourceDataPatches.set(edge.source, { ...(sourceDataPatches.get(edge.source) || {}), [field]: stepId });
          // The ifElse edge-sync effect recreates this edge once the data field above changes.
        } else if (edge.sourceHandle === 'error') {
          sourceDataPatches.set(edge.source, { ...(sourceDataPatches.get(edge.source) || {}), errorStepId: stepId });
          replacementEdges.push({
            id: `${edge.source}-error-${stepId}`,
            source: edge.source,
            target: stepId,
            sourceHandle: 'error',
            label: 'Error',
            style: { stroke: '#ef4444', strokeDasharray: '5,5' },
          });
        } else {
          replacementEdges.push({
            id: `${edge.source}-${stepId}`,
            source: edge.source,
            target: stepId,
            sourceHandle: edge.sourceHandle,
          });
        }
      });

      let endLoopNode = null;
      if (isForEach) {
        const endLoopStepId = `RS${stepCounter + 1}`;
        endLoopNode = {
          id: endLoopStepId,
          type: 'stepNode',
          position: { x: newNode.position.x, y: newNode.position.y + stepSpacing },
          data: {
            stepId: endLoopStepId,
            label: 'End Loop',
            stepType: 'endLoop',
            description: 'End loop iteration',
            action: 17,
          },
        };
        replacementEdges.push({ id: `${stepId}-${endLoopStepId}`, source: stepId, target: endLoopStepId, sourceHandle: 'next' });
        replacementEdges.push({ id: `${endLoopStepId}-${targetNode.id}`, source: endLoopStepId, target: targetNode.id, sourceHandle: 'next' });
      } else if (!isIfElse) {
        replacementEdges.push({ id: `${stepId}-${targetNode.id}`, source: stepId, target: targetNode.id, sourceHandle: 'next' });
      }
      // isIfElse: no direct edge needed here — conditionTruePath/conditionFalsePath (set above)
      // are picked up by the ifElse edge-sync effect, which creates both branch edges itself.

      setNodes((nds) => {
        const adjusted = nds.map((n) => {
          if (nodesToShift.find((s) => s.id === n.id)) {
            return { ...n, position: { x: n.position.x, y: n.position.y + shiftAmount } };
          }
          const patch = sourceDataPatches.get(n.id);
          return patch ? { ...n, data: { ...n.data, ...patch } } : n;
        });
        return endLoopNode ? [...adjusted, newNode, endLoopNode] : [...adjusted, newNode];
      });

      setEdges((eds) => [
        ...eds.filter((e) => !incomingEdges.some((inEdge) => inEdge.id === e.id)),
        ...replacementEdges,
      ]);

      setStepCounter((prev) => prev + (isForEach ? 2 : 1));
      setSelectedNode(newNode);
    },
    [nodes, edges, stepCounter, setNodes, setEdges, setStepCounter, setSelectedNode]
  );

  // Resolves insertStepPicker (opened from onNodeContextMenu's "Add Step Before"/"Add Step
  // After") once the user picks a step type from StepTypePickerModal.
  const handleInsertStepTypeSelected = useCallback(
    (stepType) => {
      if (!insertStepPicker) return;
      const targetNode = nodes.find((n) => n.id === insertStepPicker.nodeId);
      if (targetNode) {
        if (insertStepPicker.mode === 'before') {
          insertStepBefore(targetNode, stepType);
        } else {
          setSelectedNode(targetNode);
          setPendingAfterInsert(stepType);
        }
      }
      setInsertStepPicker(null);
    },
    [insertStepPicker, nodes, insertStepBefore, setSelectedNode]
  );

  // Scaffold steps from a pushed-data preview result
  // For each selected LineDataSetPointer key, inserts:
  //   ifElse (null guard) → forEach → nested getPushedData → endLoop
  // Scaffold steps from a pushed-data preview result.
  // Receives a selectedTree (recursive structure) and the root getPD stepId.
  // For each selected pointer at any depth, generates: forEach → nested getPD → [children] → endLoop.
  // Filters use {{parentStepId_pointerKey}} so the step references are correct.
  const handleScaffoldFromPreview = useCallback(({ selectedTree, rootStepId, rootFields = [] }) => {
    // selectedTree may be empty (no nested arrays chosen, or none exist) — that's fine,
    // the root forEach + "log root fields" scaffold below doesn't depend on it. Only
    // bail out if there's truly nothing to build (no nested picks AND no root fields).
    if (!selectedNode || (!selectedTree?.length && !rootFields.length)) return;

    const stepSpacing = 84; // ~60px node + ~24px gap (one row of dots); 7× the 12px grid
    const indentPerLevel = 48; // 4 grid squares (4 × 12px) of nesting indent
    const rootX = selectedNode.position.x;
    const rootY = selectedNode.position.y;

    // Existing outgoing "next" edge from the root node
    const existingNextEdge = edges.find(
      e => e.source === selectedNode.id && e.sourceHandle === 'next'
    );
    const existingNextTarget = existingNextEdge?.target || null;

    // Collect downstream node IDs to shift down
    const nodesToShift = new Set();
    if (existingNextTarget) {
      const visited = new Set();
      const queue = [existingNextTarget];
      while (queue.length > 0) {
        const id = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);
        nodesToShift.add(id);
        edges.filter(e => e.source === id).forEach(e => {
          if (!visited.has(e.target)) queue.push(e.target);
        });
      }
    }

    const allNewNodes = [];
    const allNewEdges = [];
    let counter = stepCounter;

    // Recursively generate nodes for one pointer group.
    // Each call creates: guard -> nestedPD -> forEach(nestedPD) -> log -> [children] -> innerEndLoop
    //
    // parentForEachId: the forEach step whose current record contains pointer.key as a field.
    //   Top-level pointers use outerForEachId; children use innerForEachId of their parent group.
    // nestLevel: depth level for X positioning only.
    //
    // Caller must set firstNodeId.data.conditionTruePath to the containing endLoop
    // (the endLoop that closes the forEach the caller owns).
    function generateGroup(pointer, parentForEachId, nestLevel, startY) {
      const { key: pointerKey, arrayName, children = [], fields: pointerFields = [] } = pointer;

      const guardX    = rootX + indentPerLevel * (nestLevel + 1);
      const nestedX   = rootX + indentPerLevel * (nestLevel + 2);
      const innerEndX = rootX + indentPerLevel * (nestLevel + 1);

      let posY = startY;

      // 1. guard: check if the pointer field is present in the parent forEach's current record
      const guardId = `RS${counter++}`;
      allNewNodes.push({
        id: guardId, type: 'stepNode', position: { x: guardX, y: posY },
        data: { stepId: guardId, label: `Has ${arrayName}?`, stepType: 'ifElse',
          description: `Skip ${arrayName} if pointer is empty`, action: 6,
          condition: `FX_ISNULLOREMPTY({{${parentForEachId}_${pointerKey}}})`,
          conditionTruePath: '', conditionFalsePath: '' },
      });
      posY += stepSpacing;

      // 2. nestedPD: fetch nested records filtered by the pointer value
      const nestedId = `RS${counter++}`;
      const nestedFieldsToQuery = [...pointerFields, ...children.map(c => c.key)].join(',');
      allNewNodes.push({
        id: nestedId, type: 'stepNode', position: { x: nestedX, y: posY },
        data: { stepId: nestedId, label: `Get ${arrayName} (nested)`, stepType: 'getPushedData',
          description: `Get nested ${arrayName} data`, action: 11, nestedItems: true,
          filter: `{{${parentForEachId}_${pointerKey}}}`,
          ...(nestedFieldsToQuery && { fieldsToQuery: nestedFieldsToQuery }) },
      });
      { const g = allNewNodes.find(n => n.id === guardId); if (g) g.data.conditionFalsePath = nestedId; }
      posY += stepSpacing;

      // 3. innerForEach: iterate the records returned by nestedPD
      const innerForEachId = `RS${counter++}`;
      allNewNodes.push({
        id: innerForEachId, type: 'stepNode', position: { x: nestedX, y: posY },
        data: { stepId: innerForEachId, label: `Loop: ${arrayName}`, stepType: 'forEach',
          description: `Loop through ${arrayName} records`, action: 8, dataStepId: nestedId, serialProcessing: true },
      });
      allNewEdges.push({ id: `${nestedId}-${innerForEachId}`,
        source: nestedId, target: innerForEachId, sourceHandle: 'next' });
      posY += stepSpacing;

      // 4. innerLog: list all fields available in innerForEachId's current iteration
      const innerLogId = `RS${counter++}`;
      const logFields = [...pointerFields, ...children.map(c => c.key)];
      const logMsg = logFields.map(f => `${f}: {{${innerForEachId}_${f}}}`).join(', ');
      allNewNodes.push({
        id: innerLogId, type: 'stepNode', position: { x: nestedX, y: posY },
        data: { stepId: innerLogId, label: `Log ${arrayName}`, stepType: 'logMessage',
          description: `Log ${arrayName} record fields`, action: 15,
          fieldMappings: [{ name: 'ValueToLog', value: logMsg }] },
      });
      allNewEdges.push({ id: `${innerForEachId}-${innerLogId}`,
        source: innerForEachId, target: innerLogId, sourceHandle: 'next' });
      posY += stepSpacing;

      // 5. recurse: children check {{innerForEachId_childPointerKey}} — the correct reference
      let prevInnerNodeId = innerLogId;
      const childFirstNodeIds = [];
      for (const child of children) {
        const childResult = generateGroup(child, innerForEachId, nestLevel + 1, posY);
        childFirstNodeIds.push(childResult.firstNodeId);
        allNewEdges.push({ id: `${prevInnerNodeId}-${childResult.firstNodeId}`,
          source: prevInnerNodeId, target: childResult.firstNodeId, sourceHandle: 'next' });
        posY = childResult.nextY;
        prevInnerNodeId = childResult.lastNodeId;
      }

      // 6. innerEndLoop: closes innerForEachId
      const innerEndLoopId = `RS${counter++}`;
      allNewNodes.push({
        id: innerEndLoopId, type: 'stepNode', position: { x: innerEndX, y: posY },
        data: { stepId: innerEndLoopId, label: 'End Loop', stepType: 'endLoop',
          description: 'End loop iteration', action: 17 },
      });
      allNewEdges.push({ id: `${prevInnerNodeId}-${innerEndLoopId}`,
        source: prevInnerNodeId, target: innerEndLoopId, sourceHandle: 'next' });

      // Child guards skip to innerEndLoopId when their pointer is null/empty
      for (const childGuardId of childFirstNodeIds) {
        const g = allNewNodes.find(n => n.id === childGuardId);
        if (g) g.data.conditionTruePath = innerEndLoopId;
      }

      return {
        firstNodeId: guardId,
        lastNodeId: innerEndLoopId,
        nextY: posY + stepSpacing,
      };
    }

    // One outer forEach(rootStepId) wraps all top-level pointer groups so every
    // root record is visited once before any nested data is accessed.
    let currentY = rootY + stepSpacing;

    const outerForEachId = `RS${counter++}`;
    allNewNodes.push({
      id: outerForEachId, type: 'stepNode',
      position: { x: rootX + indentPerLevel, y: currentY },
      data: { stepId: outerForEachId, label: 'Loop: root records', stepType: 'forEach',
        description: 'Loop through root records', action: 8, dataStepId: rootStepId, serialProcessing: true },
    });
    allNewEdges.push({ id: `${selectedNode.id}-${outerForEachId}`,
      source: selectedNode.id, target: outerForEachId, sourceHandle: 'next' });
    currentY += stepSpacing;

    const outerLogId = `RS${counter++}`;
    const outerLogMsg = rootFields.map(f => `${f}: {{${outerForEachId}_${f}}}`).join(', ');
    allNewNodes.push({
      id: outerLogId, type: 'stepNode',
      position: { x: rootX + indentPerLevel, y: currentY },
      data: { stepId: outerLogId, label: 'Log root fields', stepType: 'logMessage',
        description: 'Log all root-level fields', action: 15,
        fieldMappings: [{ name: 'ValueToLog', value: outerLogMsg }] },
    });
    allNewEdges.push({ id: `${outerForEachId}-${outerLogId}`,
      source: outerForEachId, target: outerLogId, sourceHandle: 'next' });
    currentY += stepSpacing;

    let prevId = outerLogId;
    const topLevelGuardIds = [];

    for (const pointer of selectedTree) {
      const result = generateGroup(pointer, outerForEachId, 0, currentY);
      topLevelGuardIds.push(result.firstNodeId);
      allNewEdges.push({
        id: `${prevId}-${result.firstNodeId}`,
        source: prevId,
        target: result.firstNodeId,
        sourceHandle: 'next',
      });
      currentY = result.nextY;
      prevId = result.lastNodeId;
    }

    const outerEndLoopId = `RS${counter++}`;
    allNewNodes.push({
      id: outerEndLoopId, type: 'stepNode',
      position: { x: rootX, y: currentY },
      data: { stepId: outerEndLoopId, label: 'End Loop', stepType: 'endLoop',
        description: 'End root loop', action: 17 },
    });
    allNewEdges.push({ id: `${prevId}-${outerEndLoopId}`,
      source: prevId, target: outerEndLoopId, sourceHandle: 'next' });

    // Top-level guards skip to outerEndLoopId when their pointer is null/empty
    for (const guardId of topLevelGuardIds) {
      const g = allNewNodes.find(n => n.id === guardId);
      if (g) g.data.conditionTruePath = outerEndLoopId;
    }

    const totalNewHeight = currentY - rootY;

    setNodes(nds => {
      const shifted = nds.map(n => {
        if (nodesToShift.has(n.id)) return { ...n, position: { ...n.position, y: n.position.y + totalNewHeight } };
        if (n.id === selectedNode.id && n.data.stepType === 'getPushedData' && rootFields.length > 0) {
          return { ...n, data: { ...n.data, fieldsToQuery: rootFields.join(',') } };
        }
        return n;
      });
      return [...shifted, ...allNewNodes];
    });

    setEdges(eds => [
      ...eds.filter(e => !existingNextEdge || e.id !== existingNextEdge.id),
      ...allNewEdges,
    ]);

    setStepCounter(counter);
    setSelectedNode(null);
  }, [selectedNode, nodes, edges, stepCounter, setNodes, setEdges, setStepCounter, setSelectedNode]);

  // Map action numbers to step types
  const actionToStepType = {
    0: 'executeRunbook',
    1: 'query',
    2: 'lookup',
    3: 'create',
    4: 'update',
    5: 'delete',
    6: 'ifElse',
    8: 'forEach',
    10: 'setVar',
    24: 'jsonSetVar',
    11: 'getPushedData',
    12: 'executeStoredProcedure',
    14: 'declareVar',
    15: 'logMessage',
    16: 'setError',
    17: 'endLoop',
    18: 'sendEmail',
    19: 'upsertClientVar',
    20: 'buildTableCache',
    21: 'lookupTableCache',
    22: 'queryTableCache',
    23: 'deleteTableCache',
  };

  // Map action numbers to proper labels
  const actionToLabel = {
    0: 'Execute Runbook',
    1: 'Query',
    2: 'Lookup',
    3: 'Create',
    4: 'Update',
    5: 'Delete',
    6: 'If/Else',
    8: 'For Each',
    10: 'Set Variable',
    24: 'JSON Set Variable',
    11: 'Get Pushed Data',
    12: 'Execute Stored Procedure',
    14: 'Declare Variable',
    15: 'Log Message',
    16: 'Set Error',
    17: 'End Loop',
    18: 'Send Email',
    19: 'Upsert Client Variable',
    20: 'Build Table Cache',
    21: 'Lookup Table Cache',
    22: 'Query Table Cache',
    23: 'Delete Table Cache',
  };

  const handleImportWorkflow = useCallback((workflowJson, options = {}) => {
    // Behavior depends on the entry point:
    //  - connectorMapping present (JSON paste import): the user explicitly
    //    mapped each imported connector to one of THEIR client's connectors in
    //    the import modal. Attach the chosen connectors and rewrite each step's
    //    connectorId to the mapped one. Variables are client-scoped and are not
    //    imported; the user re-adds them.
    //  - default (opening your own version, templates, AI hydrate): these are
    //    the current client's own resources, so load connectors and variables
    //    as saved (no filtering).
    const { connectorMapping } = options;
    const availableConnectors = clientConnectorReducer.paginatedResult?.data || [];

    // resolveStepConnector maps a step's (imported) connectorId to the connector
    // reference that should actually be stored on the step.
    let resolveStepConnector;

    if (connectorMapping) {
      // Attach the unique set of user-chosen client connectors.
      const chosen = [];
      const seen = new Set();
      Object.values(connectorMapping).forEach((c) => {
        if (c && !seen.has(c.connectorId)) {
          seen.add(c.connectorId);
          chosen.push(c);
        }
      });
      setRunbookConnectors(chosen);
      // Variables are client-scoped and not imported; the user re-adds them.
      setRunbookVariables([]);
      setShowImportStrippedNotice(
        Array.isArray(workflowJson.runbookVariables) && workflowJson.runbookVariables.length > 0
      );

      resolveStepConnector = (importedId) => {
        const mapped = connectorMapping[importedId];
        return mapped
          ? { connectorId: mapped.connectorId, connectorNumber: mapped.connectorNumber || '' }
          : { connectorId: '', connectorNumber: '' };
      };
    } else {
      setShowImportStrippedNotice(false);

      // Load connectors as saved, enriching with current client data when it
      // is available (falls back to the saved copy if not yet loaded).
      if (workflowJson.connectors && Array.isArray(workflowJson.connectors)) {
        const enrichedConnectors = workflowJson.connectors.map((savedConnector) => {
          const currentConnector = availableConnectors.find(
            (c) => c.connectorId === savedConnector.connectorId
          );
          return currentConnector || savedConnector;
        });
        setRunbookConnectors(enrichedConnectors);
      }

      if (workflowJson.runbookVariables && Array.isArray(workflowJson.runbookVariables)) {
        setRunbookVariables(workflowJson.runbookVariables);
      }

      resolveStepConnector = (importedId, importedNumber) => ({
        connectorId: importedId || '',
        connectorNumber: importedNumber || '',
      });
    }

    // Import steps and create nodes
    const importedNodes = [];
    const importedEdges = [];
    const groupsData = workflowJson.groups || [];

    // Create a start node if not present
    const hasStartNode = workflowJson.steps.some(step => step.action === 0 || step.stepNumber === 'start');
    if (!hasStartNode) {
      importedNodes.push({
        id: 'start',
        type: 'stepNode',
        position: { x: 250, y: 50 },
        data: {
          label: 'Start',
          stepType: 'start',
          description: 'Workflow Start',
        },
      });
    }

    // Build a map to track loop depths for positioning
    const loopDepthMap = {};
    workflowJson.steps.forEach((step) => {
      // Normalize parentStepId: treat null, undefined, and empty string as no parent
      const parentId = step.parentStepId || '';
      if (parentId) {
        const parentDepth = loopDepthMap[parentId] || 0;
        loopDepthMap[step.stepNumber] = parentDepth + 1;
      } else {
        loopDepthMap[step.stepNumber] = 0;
      }
    });

    // #2662: stepType by stepNumber for every step up front, since an edge's target can be a
    // forward reference not yet converted to a node by the time its edge is created below.
    const stepTypeByStepNumber = {};
    workflowJson.steps.forEach((step) => {
      stepTypeByStepNumber[step.stepNumber] = actionToStepType[step.action] || 'query';
    });
    const importedEndLoopEdgeStyle = (tgtId, baseStyle) =>
      stepTypeByStepNumber[tgtId] === 'endLoop' ? { ...baseStyle, ...END_LOOP_PURPLE_STYLE } : baseStyle;

    // Create nodes from steps
    workflowJson.steps.forEach((step, index) => {
      const stepType = actionToStepType[step.action] || 'query';
      const stepLabel = actionToLabel[step.action] || 'Query';
      const loopDepth = loopDepthMap[step.stepNumber] || 0;

      // Use saved position if available, otherwise calculate position
      const hasStoredPosition = step.positionX !== undefined && step.positionY !== undefined;

      // Normalize fieldMappings: only keep name and value (strip out all the extra old system fields)
      let normalizedFieldMappings = (step.fieldMappings || []).map(fm => ({
        name: fm.name || fm.fieldName || fm.FieldName,
        value: fm.value || ''
      }));

      // Handle SetVar step - extract setVarValue from fieldMappings
      let setVarValue = step.setVarValue || '';
      if (stepType === 'setVar' && normalizedFieldMappings.length > 0) {
        // In old system, SetVar stores value in first fieldMapping
        const firstMapping = normalizedFieldMappings[0];
        if (firstMapping && firstMapping.value) {
          setVarValue = firstMapping.value;
          // Don't include this in fieldMappings as it's now in setVarValue
          normalizedFieldMappings = [];
        }
      }

      // Handle LogMessage step - normalize fieldMapping to ValueToLog
      if (stepType === 'logMessage' && normalizedFieldMappings.length > 0) {
        // In old system, LogMessage stores value with empty fieldName
        const firstMapping = normalizedFieldMappings[0];
        if (firstMapping && firstMapping.value) {
          normalizedFieldMappings = [{
            name: 'ValueToLog',
            value: firstMapping.value
          }];
        }
      }

      // For jsonSetVar, parse type suffix from field names (e.g., amount_int → name: amount, fieldType: int)
      if (stepType === 'jsonSetVar') {
        const typeSuffixes = ['int', 'bool', 'decimal', 'datetime', 'json'];
        normalizedFieldMappings = normalizedFieldMappings.map(fm => {
          const name = fm.name || '';
          const lastUnderscore = name.lastIndexOf('_');
          if (lastUnderscore > 0) {
            const suffix = name.substring(lastUnderscore + 1);
            if (typeSuffixes.includes(suffix)) {
              return { ...fm, name: name.substring(0, lastUnderscore), fieldType: suffix };
            }
          }
          return { ...fm, fieldType: 'string' };
        });
      }

      // For If/Else steps: if nextStepId exists but conditionTruePath doesn't, convert nextStepId to conditionTruePath
      let conditionTruePath = step.conditionTruePath || '';
      let conditionFalsePath = step.conditionFalsePath || '';

      if (stepType === 'ifElse') {
        if (!conditionTruePath && step.nextStepId) {
          // Convert nextStepId to conditionTruePath on import
          conditionTruePath = step.nextStepId;
        }
      }

      // For CRUD steps (query, create, update, delete, lookup), append system name to label if no custom name
      const crudStepTypes = ['query', 'create', 'update', 'delete', 'lookup', 'executeStoredProcedure'];
      // Prioritize user's saved stepName, then auto-generate for CRUD steps, then default
      const displayLabel = step.stepName
        || (crudStepTypes.includes(stepType) && step.system
            ? `${stepLabel} - ${step.system}`
            : stepLabel);

      const node = {
        id: step.stepNumber,
        type: 'stepNode',
        position: hasStoredPosition ? {
          x: step.positionX,
          y: step.positionY,
        } : {
          x: 250 + (loopDepth * 48), // Indent nested loops horizontally (4 grid squares)
          y: 150 + (index * 84), // Vertical spacing (~60px node + ~24px gap)
        },
        data: {
          stepId: step.stepNumber,
          label: displayLabel,
          stepType: stepType,
          description: step.stepDescription || '',
          action: step.action,
          stepOrder: step.stepOrder || 0,
          parentStepId: step.parentStepId || '',
          groupId: step.groupId || '',
          // Rewrite the step's connector reference to the mapped/current-client
          // connector; blanks it when there is no valid mapping so a
          // foreign/dangling connectorId is never carried into this runbook.
          connectorId: resolveStepConnector(step.connectorId, step.connectorNumber).connectorId,
          connectorNumber: resolveStepConnector(step.connectorId, step.connectorNumber).connectorNumber,
          systemObject: step.systemObject || '',
          system: step.system || '',
          fieldsToQuery: step.fieldsToQuery || '',
          filter: step.filter || '',
          condition: step.condition || '',
          dataStepId: step.dataStepId || step.dataStep || '',
          fieldMappings: normalizedFieldMappings,
          customErrorMessage: step.customErrorMessage || '',
          errorUponCompletion: step.errorUponCompletion || false,
          continueAfterError: step.continueAfterError || false,
          netChange: step.netChange || false,
          inspectRequest: step.inspectRequest || false,
          serialProcessing: step.serialProcessing || false,
          setVarType: step.setVarType || '',
          setVarValue: setVarValue,
          offsetRecordProcessing: step.offsetRecordProcessing || false,
          offset: step.offsetSeconds || 0,
          debugMode: step.debugMode || false,
          debugStartingAt: step.debugStartingAt || 0,
          debugNumberOfRecords: step.debugNumberToProcess || 0,
          conditionTruePath: conditionTruePath,
          conditionFalsePath: conditionFalsePath,
          errorStepId: step.errorStepId || '',
          nestedItems: step.nestedItems || false,
        },
      };

      importedNodes.push(node);

      // Create edges based on connections
      // Skip 'next' edge for If/Else steps (they only use true/false edges)
      // edgeWaypoints is purely cosmetic (manually-dragged waypoints, see DraggableEdge) —
      // round-tripped through the same opaque RunbookString JSON blob as positionX/positionY,
      // never touched by the execution engine.
      const edgeWaypoints = step.edgeWaypoints || {};

      if (step.nextStepId && stepType !== 'ifElse') {
        const nextStyle = importedEndLoopEdgeStyle(step.nextStepId, undefined);
        importedEdges.push({
          id: `${step.stepNumber}-${step.nextStepId}`,
          source: step.stepNumber,
          target: step.nextStepId,
          sourceHandle: 'next',
          ...(nextStyle && { style: nextStyle }),
          data: { waypoints: edgeWaypoints.next || [] },
        });
      }

      // If/Else conditional branches (use the converted conditionTruePath from above)
      if (conditionTruePath) {
        importedEdges.push({
          id: `${step.stepNumber}-true-${conditionTruePath}`,
          source: step.stepNumber,
          target: conditionTruePath,
          sourceHandle: 'true',
          label: 'True',
          style: importedEndLoopEdgeStyle(conditionTruePath, { stroke: '#10b981' }),
          data: { waypoints: edgeWaypoints.true || [] },
        });
      }

      if (conditionFalsePath) {
        importedEdges.push({
          id: `${step.stepNumber}-false-${conditionFalsePath}`,
          source: step.stepNumber,
          target: conditionFalsePath,
          sourceHandle: 'false',
          label: 'False',
          style: importedEndLoopEdgeStyle(conditionFalsePath, { stroke: '#f59e0b' }),
          data: { waypoints: edgeWaypoints.false || [] },
        });
      }

      // Error path
      if (step.errorStepId) {
        importedEdges.push({
          id: `${step.stepNumber}-error-${step.errorStepId}`,
          source: step.stepNumber,
          target: step.errorStepId,
          sourceHandle: 'error',
          label: 'Error',
          style: { stroke: '#ef4444', strokeDasharray: '5,5' },
          data: { waypoints: edgeWaypoints.error || [] },
        });
      }
    });

    // Connect start node to first step if exists
    if (!hasStartNode && workflowJson.steps.length > 0) {
      const firstStep = workflowJson.steps[0];
      if (firstStep) {
        importedEdges.push({
          id: `start-${firstStep.stepNumber}`,
          source: 'start',
          target: firstStep.stepNumber,
          sourceHandle: 'next',
        });

        // Position the start node directly above the first step
        const firstStepNode = importedNodes.find(n => n.id === firstStep.stepNumber);
        const startNode = importedNodes.find(n => n.id === 'start');
        if (firstStepNode && startNode) {
          startNode.position = {
            x: firstStepNode.position.x,
            y: firstStepNode.position.y - 84, // one step-spacing above (~60px node + ~24px gap)
          };
        }
      }
    }

    // Recreate groups from groupId properties and groups array
    const groupsMap = {};
    importedNodes.forEach(node => {
      if (node.data.groupId) {
        if (!groupsMap[node.data.groupId]) {
          groupsMap[node.data.groupId] = {
            nodes: [],
            name: node.data.groupId
          };
        }
        groupsMap[node.data.groupId].nodes.push(node);
      }
    });

    // Merge with groups data from JSON
    groupsData.forEach(group => {
      if (groupsMap[group.groupId]) {
        groupsMap[group.groupId].name = group.groupName;
      }
    });

    // Create group nodes and update child nodes
    Object.keys(groupsMap).forEach(groupId => {
      const groupData = groupsMap[groupId];
      const groupedNodes = groupData.nodes;

      // Calculate bounding box with minimal padding
      const minX = Math.min(...groupedNodes.map(n => n.position.x)) - 20;
      const minY = Math.min(...groupedNodes.map(n => n.position.y)) - 50;
      const maxX = Math.max(...groupedNodes.map(n => n.position.x + 200)); // Node width
      const maxY = Math.max(...groupedNodes.map(n => n.position.y + 100)); // Node height

      // Create group node
      const groupNode = {
        id: groupId,
        type: 'groupNode',
        position: { x: minX, y: minY },
        style: {
          width: maxX - minX + 40,
          height: maxY - minY + 30,
        },
        data: {
          label: groupData.name,
        },
        zIndex: -1,
      };

      importedNodes.push(groupNode);

      // Update child nodes positions to be relative to group
      groupedNodes.forEach(node => {
        node.position = {
          x: node.position.x - minX,
          y: node.position.y - minY,
        };
        node.parentNode = groupId;
        node.extent = 'parent';
        node.zIndex = 1; // Ensure child nodes are above the group node for click events
      });
    });

    // Reconstruct free-floating annotation notes (not part of the step graph or any group)
    (workflowJson.notes || []).forEach(note => {
      importedNodes.push({
        id: note.noteId,
        type: 'noteNode',
        position: { x: note.positionX || 0, y: note.positionY || 0 },
        data: { text: note.text || '' },
        style: { width: note.width || 200, height: note.height || 120 },
      });
    });

    setNodes(importedNodes);
    setEdges(importedEdges);

    // An import replaces the canvas wholesale (AI hydrate, paste-import, opening a version).
    // Clear any open step-config selection AND its pending edits: otherwise a panel that was open
    // over the previous canvas holds stale local state (pendingNodeConfig) that gets written back
    // onto the freshly-imported node on the next select/close (see handleNodeClick / panel onClose),
    // silently reverting the change. This was the "AI edit doesn't show on the canvas" bug — the
    // server returned the correct updated step, but the open panel overwrote it right back.
    setSelectedNode(null);
    setPendingNodeConfig(null);

    // Update step counter to be one more than the highest step number
    const maxStepNum = Math.max(
      ...workflowJson.steps
        .map(s => parseInt(s.stepNumber.replace('RS', '')))
        .filter(n => !isNaN(n)),
      0
    );
    setStepCounter(maxStepNum + 1);

    // Update group counter based on imported groups
    const maxGroupNum = Math.max(
      ...Object.keys(groupsMap).map(g => parseInt(g.replace('RG', ''))).filter(n => !isNaN(n)),
      0
    );
    setGroupCounter(maxGroupNum + 1);
  }, [setNodes, setEdges, clientConnectorReducer, setSelectedNode, setPendingNodeConfig]);

  // Auto-import workflow from sessionStorage if present
  useEffect(() => {
    const importWorkflowData = sessionStorage.getItem('importWorkflow');
    if (importWorkflowData) {
      try {
        const workflowJson = JSON.parse(importWorkflowData);
        handleImportWorkflow(workflowJson);
        // Baseline for "nothing changed" — capture the opened content BEFORE it is cleared below.
        setLastAppliedJson(importWorkflowData);
        // Clear the session storage after import
        sessionStorage.removeItem('importWorkflow');
      } catch (error) {
        sessionStorage.removeItem('importWorkflow');
      }
    }

    // Load editing version metadata if present
    const editingVersionData = sessionStorage.getItem('editingVersion');
    if (editingVersionData) {
      try {
        const versionData = JSON.parse(editingVersionData);
        setEditingVersion(versionData);
        setIsReadOnly(versionData.readOnly || false);
      } catch (error) {
        sessionStorage.removeItem('editingVersion');
      }
    }

    // Signals the close-guard's baseline-capture effect below that the initial load has been
    // dispatched — the setNodes/setEdges calls inside handleImportWorkflow (if anything was
    // imported) batch into the SAME commit as this update, so by the next render nodes/edges
    // already reflect the loaded content.
    setDesignerHydrated(true);
  }, [handleImportWorkflow]);

  const generateWorkflowJson = useCallback(() => {
    // Merge pending config into nodes if there's an unsaved config in the panel
    // This ensures changes in the step config panel (including advanced expression editor)
    // are included even if the user hasn't clicked elsewhere to commit them
    const effectiveNodes = (selectedNode && pendingNodeConfig)
      ? nodes.map(node =>
          node.id === selectedNode.id
            ? { ...node, data: { ...node.data, ...pendingNodeConfig } }
            : node
        )
      : nodes;

    // Which forEach loop each step belongs to (see loopParenting.js)
    const parentStepMap = computeParentStepMap(effectiveNodes, edges);

    // Topological sort to determine step order
    const sortedSteps = [];
    const visited = new Set();
    const temp = new Set();

    const visit = (nodeId) => {
      if (temp.has(nodeId)) return; // Cycle detection
      if (visited.has(nodeId)) return;

      temp.add(nodeId);

      // Visit all nodes that must come before this one (incoming edges)
      const incomingEdges = edges.filter(e => e.target === nodeId);
      incomingEdges.forEach(edge => {
        visit(edge.source);
      });

      temp.delete(nodeId);
      visited.add(nodeId);

      const node = effectiveNodes.find(n => n.id === nodeId);
      if (node && node.id !== 'start' && node.type !== 'groupNode' && node.type !== 'noteNode') {
        sortedSteps.push(node);
      }
    };

    // Seed the sort from whatever 'start' is actually connected to, so reconnecting it to a
    // different step reliably makes THAT step run first (stepOrder 10) — otherwise the
    // fallback loop below visits nodes in array/creation order, unrelated to the graph's
    // actual wiring, and whichever node happens to come first there keeps being treated as
    // first regardless of what 'start' now points to.
    const startEdge = edges.find(e => e.source === 'start');
    if (startEdge) visit(startEdge.target);

    // Then sweep the rest (nodes already visited above are skipped by visit()'s own guard).
    effectiveNodes.forEach(node => {
      if (node.id !== 'start' && node.type !== 'groupNode' && node.type !== 'noteNode') {
        visit(node.id);
      }
    });

    return {
      connectors: runbookConnectors.map(connector => ({
        connectorId: connector.connectorId,
        connectorNumber: connector.connectorNumber,
        system: connector.system,
        description: connector.description
      })),
      runbookVariables: runbookVariables,
      groups: effectiveNodes
        .filter(node => node.type === 'groupNode')
        .map(node => ({
          groupId: node.id,
          groupName: node.data.label,
        })),
      // Free-floating annotations — purely cosmetic, never seen by the execution engine, so
      // they get their own section rather than living in steps (which drives stepOrder).
      notes: effectiveNodes
        .filter(node => node.type === 'noteNode')
        .map(node => ({
          noteId: node.id,
          text: node.data.text || '',
          positionX: node.position.x,
          positionY: node.position.y,
          width: node.style?.width,
          height: node.style?.height,
        })),
      steps: sortedSteps.map((node, index) => {
        // Find the next step by looking at edges
        const nextEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'next');
        const trueEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'true');
        const falseEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'false');
        const errorEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'error');

        // Manually-dragged waypoints (see DraggableEdge) — purely cosmetic, round-tripped
        // the same way positionX/positionY are, only included when actually used so most steps
        // stay free of it.
        const edgeWaypoints = {};
        if (nextEdge?.data?.waypoints?.length) edgeWaypoints.next = nextEdge.data.waypoints;
        if (trueEdge?.data?.waypoints?.length) edgeWaypoints.true = trueEdge.data.waypoints;
        if (falseEdge?.data?.waypoints?.length) edgeWaypoints.false = falseEdge.data.waypoints;
        if (errorEdge?.data?.waypoints?.length) edgeWaypoints.error = errorEdge.data.waypoints;

        // Generate description dynamically for forEach loops
        let stepDescription = node.data.description;
        if (node.data.stepType === 'forEach') {
          let dataStepId = node.data.dataStepId;

          // Auto-detect if not manually set
          if (!dataStepId) {
            const incomingEdges = edges.filter(e => e.target === node.id);
            if (incomingEdges.length > 0) {
              const sourceNode = effectiveNodes.find(n => n.id === incomingEdges[0].source);
              if (sourceNode &&
                  (sourceNode.data.stepType === 'query' ||
                   sourceNode.data.stepType === 'queryTableCache')) {
                dataStepId = sourceNode.data.stepId || sourceNode.id;
              }
            }
          }

          // Generate description from the data step's system object
          if (dataStepId) {
            const dataStepNode = effectiveNodes.find(n => (n.data.stepId || n.id) === dataStepId);
            if (dataStepNode && dataStepNode.data.systemObject) {
              stepDescription = `Iterate through ${dataStepNode.data.systemObject}`;
            }
          }
        }

        // Handle SetVar step - convert setVarValue to fieldMappings
        let fieldMappings = node.data.fieldMappings || [];
        if (node.data.stepType === 'setVar' && node.data.setVarValue) {
          fieldMappings = [{
            fieldName: 'setvarmapping',
            fieldRequired: false,
            isJson: false,
            value: node.data.setVarValue
          }];
        }

        // Calculate absolute position (if node is in a group, add group's position)
        let absoluteX = node.position.x;
        let absoluteY = node.position.y;
        if (node.parentNode) {
          const parentGroupNode = effectiveNodes.find(n => n.id === node.parentNode && n.type === 'groupNode');
          if (parentGroupNode) {
            absoluteX = node.position.x + parentGroupNode.position.x;
            absoluteY = node.position.y + parentGroupNode.position.y;
          }
        }

        const stepNumber = node.data.stepId || node.id;
        const runbookId = editingVersion?.runbookId || '';

        // For jsonSetVar, append type suffix to field names (e.g., amount_int)
        if (node.data.stepType === 'jsonSetVar') {
          fieldMappings = fieldMappings.map(mapping => {
            const fieldType = mapping.fieldType || 'string';
            const baseName = mapping.name || '';
            return {
              ...mapping,
              name: fieldType !== 'string' && baseName ? `${baseName}_${fieldType}` : baseName,
            };
          });
        }

        // Transform field mappings: rename "name" to "FieldName" and add RunbookId and StepId
        const transformedFieldMappings = fieldMappings.map(mapping => {
          const transformed = { ...mapping };

          // Rename "name" to "FieldName"
          if (transformed.name !== undefined) {
            transformed.FieldName = transformed.name;
            delete transformed.name;
          }

          // Remove fieldType from the output (UI-only property)
          delete transformed.fieldType;

          // Add RunbookId and StepId
          transformed.RunbookId = runbookId;
          transformed.StepId = stepNumber;

          return transformed;
        });

        const step = {
          stepNumber: stepNumber,
          stepOrder: (index + 1) * 10, // Start at 10, increment by 10
          stepName: node.data.label,
          stepDescription: stepDescription,
          action: node.data.action,
          nextStepId: nextEdge ? nextEdge.target : '',
          parentStepId: parentStepMap.get(node.id) || '',
          groupId: node.data.groupId || '',
          connectorId: node.data.connectorId || '',
          connectorNumber: node.data.connectorNumber || '',
          systemObject: node.data.systemObject || '',
          system: node.data.system || '',
          fieldsToQuery: node.data.fieldsToQuery || '',
          filter: node.data.filter || '',
          condition: node.data.condition || '',
          fieldMappings: transformedFieldMappings,
          customErrorMessage: node.data.customErrorMessage || '',
          netChange: node.data.netChange || false,
          inspectRequest: node.data.inspectRequest || false,
          serialProcessing: node.data.serialProcessing || false,
          errorUponCompletion: node.data.errorUponCompletion || false,
          continueAfterError: node.data.continueAfterError || false,
          errorStepId: node.data.errorStepId || '',
          runbookId: editingVersion?.runbookId || '',
          dataStep: node.data.dataStepId || '', // Add dataStep to every step with empty string default
          // Save absolute position for later import (add group position if node is grouped)
          positionX: absoluteX,
          positionY: absoluteY,
        };

        if (Object.keys(edgeWaypoints).length > 0) {
          step.edgeWaypoints = edgeWaypoints;
        }

        // Add getPushedData-specific fields
        if (node.data.stepType === 'getPushedData') {
          step.nestedItems = node.data.nestedItems || false;
        }

        // Add forEach-specific fields
        if (node.data.stepType === 'forEach') {
          // Auto-detect dataStep if not manually set
          if (!step.dataStep) {
            // Find incoming edges to this forEach node
            const incomingEdges = edges.filter(e => e.target === node.id);
            if (incomingEdges.length > 0) {
              // Get the source node
              const sourceNode = effectiveNodes.find(n => n.id === incomingEdges[0].source);
              if (sourceNode &&
                  (sourceNode.data.stepType === 'query' ||
                   sourceNode.data.stepType === 'queryTableCache')) {
                step.dataStep = sourceNode.data.stepId || sourceNode.id;
              }
            }
          }

          // Add offset record processing
          if (node.data.offsetRecordProcessing) {
            step.offsetRecordProcessing = true;
            step.offsetSeconds = node.data.offset || 0;
          }

          // Add debug mode settings
          if (node.data.debugMode) {
            step.debugMode = true;
            step.debugStartingAt = node.data.debugStartingAt || 0;
            step.debugNumberToProcess = node.data.debugNumberOfRecords || 0;
          }
        }

        // Add setVarType for setVar and jsonSetVar
        if (node.data.stepType === 'setVar') {
          if (node.data.setVarType) {
            step.setVarType = node.data.setVarType;
          }
        }
        if (node.data.stepType === 'jsonSetVar') {
          step.setVarType = 'AppendCSV';
        }

        // Add conditional paths ONLY for if/else steps
        if (node.data.stepType === 'ifElse') {
          // For If/Else: use node data properties directly (not visual edges)
          // This ensures the properties set during node creation are preserved
          if (node.data.conditionTruePath) {
            step.conditionTruePath = node.data.conditionTruePath;
          } else if (trueEdge) {
            // Fall back to visual edge if property not set
            step.conditionTruePath = trueEdge.target;
          } else if (nextEdge) {
            // If no true edge but there's a next edge, use it as the true path
            step.conditionTruePath = nextEdge.target;
          }

          if (node.data.conditionFalsePath) {
            step.conditionFalsePath = node.data.conditionFalsePath;
          } else if (falseEdge) {
            // Fall back to visual edge if property not set
            step.conditionFalsePath = falseEdge.target;
          }

          // Always clear nextStepId for If/Else steps on export
          step.nextStepId = '';
        }
        // Note: Non-If/Else steps should NEVER have conditionTruePath/conditionFalsePath exported,
        // even if they have 'true' or 'false' edges (which are only used internally for stepOrder calculation)

        return step;
      })
    };
  }, [nodes, edges, runbookConnectors, runbookVariables, editingVersion, selectedNode, pendingNodeConfig]);

  const handleExportWorkflow = useCallback(() => {
    const workflowJson = generateWorkflowJson();

    // Download as JSON file
    const jsonString = JSON.stringify(workflowJson, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [generateWorkflowJson]);

  // #1977 (Apply model): Apply builds the request that creates a NEW version from the current canvas.
  // It does NOT overwrite the version you opened — the new (highest) version becomes the live one.
  const buildApplyRequest = useCallback(() => {
    const workflowJson = generateWorkflowJson();
    const runbookString = JSON.stringify(workflowJson);
    return {
      runbookId: editingVersion.runbookId,
      runbookGuid: editingVersion.runbookGuid,
      versionNum: editingVersion.versionNum,
      description: (applyDescription && applyDescription.trim()) ? applyDescription.trim() : 'Applied',
      runbookString: runbookString,
      createdBy: props.user?.email,
      clientId: props.user?.clientId,
      clientGuid: props.user?.clientGuid,
    };
  }, [editingVersion, generateWorkflowJson, applyDescription]);

  // #1977: leave the builder back to My Runbooks, pre-selecting the runbook we were editing so the
  // list lands with that row highlighted. Used by Apply & Close, the no-change close, and Discard.
  const exitToMyRunbooks = useCallback(() => {
    if (editingVersion) {
      sessionStorage.setItem('selectRunbook', JSON.stringify({
        runbookId: editingVersion.runbookId,
        runbookGuid: editingVersion.runbookGuid,
      }));
    }
    sessionStorage.removeItem('editingVersion');
    sessionStorage.removeItem('importWorkflow');
    if (onDone) {
      onDone();
    } else {
      navigate('/runbook');
    }
  }, [editingVersion, navigate, onDone]);

  const handleApply = useCallback(async () => {
    if (!editingVersion) return;

    try {
      setSaving(true);
      const applyReq = buildApplyRequest();
      await runbookVersionService.create(applyReq);
      setLastAppliedJson(applyReq.runbookString);
      // Plain Apply (no close) leaves the window open — refresh the close-guard's own
      // baseline too, or it would still think there are unsaved changes right after a
      // successful save.
      designerBaselineRef.current = generateWorkflowJson();

      // The new version is now the highest. Re-point the builder at it so further edits + Apply
      // stack on top of the version we just created (each Apply mints one new version).
      try {
        const response = await runbookVersionService.get({
          runbookId: editingVersion.runbookId,
          runbookGuid: editingVersion.runbookGuid,
        });
        const versions = response?.data;
        const latest = Array.isArray(versions) && versions.length
          ? versions.reduce((a, b) => (b.versionNum > a.versionNum ? b : a))
          : null;
        if (latest) {
          const updated = { ...editingVersion, versionId: latest.versionId, versionNum: latest.versionNum };
          setEditingVersion(updated);
          sessionStorage.setItem('editingVersion', JSON.stringify(updated));
          // #1977: validate the newly-applied (highest) version → updates RunbookTable.ISVALID.
          // NON-BLOCKING: the version stays applied whether or not it validates. If it's invalid we
          // just surface a heads-up (it won't RUN until fixed) — we never stop the user.
          const vres = await runbookService.validate({
            runbookId: editingVersion.runbookId,
            clientId: props.user?.clientId,
            runbookGuid: editingVersion.runbookGuid,
            clientGuid: props.user?.clientGuid,
            versionNum: latest.versionNum,
            UpdateRunbookHeader: true,
          });
          if (vres?.data?.hasErrors) {
            setApplyInvalidMessage(vres.data.message || 'This version did not pass validation, so it will not run until the issues are fixed.');
            setShowApplyInvalidModal(true);
          }
        }
      } catch { /* renumber/validate is best-effort; the version was created regardless */ }

      setSaveSuccess(true);
      setShowSaveToast(true);
    } catch (error) {
      setSaveSuccess(false);
      setShowSaveToast(true);
    } finally {
      setSaving(false);
    }
  }, [editingVersion, buildApplyRequest]);

  const handleApplyAndClose = useCallback(async () => {
    if (!editingVersion) return;

    try {
      setSaving(true);
      const applyReq = buildApplyRequest();
      await runbookVersionService.create(applyReq);
      setLastAppliedJson(applyReq.runbookString);
      // Same as plain Apply above: refresh the close-guard's baseline now, or the Workspace
      // desktop's window-close check (handleCloseWindow -> hasUnsavedChanges(), since
      // exitToMyRunbooks below calls onDone rather than unmounting this component when
      // embedded there) would still see the pre-apply baseline and wrongly prompt "unsaved
      // changes" immediately after a successful Apply & Close.
      designerBaselineRef.current = generateWorkflowJson();

      // #1977: validate the newly-applied (highest) version and AWAIT it, so RunbookTable.ISVALID and
      // the My Runbooks list land accurate. NON-BLOCKING: we return regardless of the result — a user
      // mid-flight can close an invalid/WIP version; it simply won't run until it's valid.
      try {
        const response = await runbookVersionService.get({
          runbookId: editingVersion.runbookId,
          runbookGuid: editingVersion.runbookGuid,
        });
        const versions = response?.data;
        const latest = Array.isArray(versions) && versions.length
          ? versions.reduce((a, b) => (b.versionNum > a.versionNum ? b : a))
          : null;
        if (latest) {
          const vres = await runbookService.validate({
            runbookId: editingVersion.runbookId,
            clientId: props.user?.clientId,
            runbookGuid: editingVersion.runbookGuid,
            clientGuid: props.user?.clientGuid,
            versionNum: latest.versionNum,
            UpdateRunbookHeader: true,
          });
          if (vres?.data?.hasErrors) {
            // Surface the invalid result on the way out — non-blocking: dismissing the heads-up
            // navigates to My Runbooks (see dismissApplyInvalid). The apply still stands.
            setApplyInvalidMessage(vres.data.message || 'This version did not pass validation, so it will not run until the issues are fixed.');
            setShowApplyInvalidModal(true);
            setSaving(false);
            return;
          }
        }
      } catch { /* validation is best-effort; closing is never blocked */ }

      // Valid (or validation couldn't run) → back to My Runbooks with this runbook pre-selected.
      exitToMyRunbooks();
    } catch (error) {
      alert('Failed to apply version. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editingVersion, buildApplyRequest, exitToMyRunbooks]);

  // Whether the canvas has edits not reflected in the last-applied/last-loaded version —
  // same canonical comparison openApplyModal uses to decide there's "nothing to apply".
  // Exposed via ref so a host that closes this window from the outside (e.g. the Workspace
  // desktop's window-close button, which bypasses Apply & Close/Discard entirely) can check
  // before discarding canvas edits.
  // Captures the close-guard's baseline via generateWorkflowJson() itself (not the raw
  // sessionStorage string lastAppliedJson uses) once nodes/edges have settled after the
  // initial load — comparing generated JSON against generated JSON avoids false positives
  // from anything generateWorkflowJson recomputes on import (derived fields like
  // continueAfterError, stepOrder, etc.) that would otherwise look like an edit even though
  // the user hasn't touched anything. Captured once per mount — a fresh runbook loaded into
  // the Designer gets a fresh WorkflowBuilder instance (see WorkspaceHub), so there's no
  // stale-baseline-across-runbooks concern here.
  useEffect(() => {
    if (designerHydrated && designerBaselineRef.current === null) {
      designerBaselineRef.current = generateWorkflowJson();
    }
  }, [designerHydrated, generateWorkflowJson]);

  // Computed fresh on every call rather than memoized: Apply & Close refreshes
  // designerBaselineRef.current by mutating the ref directly (see handleApply/
  // handleApplyAndClose), which doesn't change nodes/edges and so wouldn't invalidate a
  // useMemo'd result — the imperative-handle caller would keep reading the boolean cached
  // from before the apply and wrongly see "unsaved changes" immediately after a successful one.
  const getHasUnsavedChanges = useCallback(() => {
    if (designerBaselineRef.current === null) return false; // not hydrated yet — nothing to compare
    try {
      return canonicalizeWorkflowJson(generateWorkflowJson()) !== canonicalizeWorkflowJson(designerBaselineRef.current);
    } catch {
      return false;
    }
  }, [generateWorkflowJson]);

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: getHasUnsavedChanges,
  }), [getHasUnsavedChanges]);

  // #1977: open the description prompt before applying. closeAfter=true → Apply & Close.
  const openApplyModal = useCallback((closeAfter) => {
    if (!editingVersion) return;

    // #1977: if nothing has changed since the last Apply (or since opening), there is nothing to
    // apply — don't prompt for a description and don't create a duplicate version.
    const currentCanonical = canonicalizeWorkflowJson(generateWorkflowJson());
    const baselineCanonical = lastAppliedJson !== null ? canonicalizeWorkflowJson(lastAppliedJson) : null;

    if (baselineCanonical !== null && currentCanonical === baselineCanonical) {
      if (closeAfter) {
        exitToMyRunbooks();
      } else {
        setSaveSuccess(true);
        setShowSaveToast(true);
      }
      return;
    }

    setPendingApplyClose(closeAfter);
    setApplyDescription(editingVersion.description || '');
    setShowApplyModal(true);
  }, [editingVersion, generateWorkflowJson, lastAppliedJson, exitToMyRunbooks]);

  const confirmApply = useCallback(async () => {
    setShowApplyModal(false);
    if (pendingApplyClose) {
      await handleApplyAndClose();
    } else {
      await handleApply();
    }
  }, [pendingApplyClose, handleApply, handleApplyAndClose]);

  // #1977: dismiss the "applied but not valid" heads-up. For an Apply & Close flow this is where the
  // navigation happens (deferred so the user sees the invalid result first); for a plain Apply it
  // just closes the modal and leaves them in the builder.
  const dismissApplyInvalid = useCallback(() => {
    setShowApplyInvalidModal(false);
    if (pendingApplyClose) exitToMyRunbooks();
  }, [pendingApplyClose, exitToMyRunbooks]);

  const handleValidate = useCallback(async () => {
    if (!editingVersion) return;

    try {
      // Save first before validating
      setSaving(true);
      const workflowJson = generateWorkflowJson();
      const runbookString = JSON.stringify(workflowJson);

      const saveRequestBody = {
        versionId: editingVersion.versionId,
        runbookId: editingVersion.runbookId,
        runbookGuid: editingVersion.runbookGuid,
        runbookString: runbookString,
        clientId: props.user?.clientId,
        clientGuid: props.user?.clientGuid,
      };

      await runbookVersionService.update(saveRequestBody);
      setSaving(false);

      // Now validate
      setShowValidateModal(true);

      // #1977: the builder always edits the live (highest) version, so validating it must ALWAYS
      // update RunbookTable.ISVALID — that header flag is what the runtime uses to decide the
      // runbook is allowed to run.
      const shouldUpdateHeader = true;

      const validateRequestBody = {
        runbookId: editingVersion.runbookId,
        clientId: props.user?.clientId,
        runbookGuid: editingVersion.runbookGuid,
        clientGuid: props.user?.clientGuid,
        versionNum: editingVersion.versionNum,
        UpdateRunbookHeader: shouldUpdateHeader,
      };

      dispatch(runbookActions.validate(validateRequestBody));
    } catch (error) {
      setSaving(false);
    }
  }, [editingVersion, generateWorkflowJson, props.user, dispatch]);

  const handleDiscardClick = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    if (!editingVersion) return;
    // Discard unsaved canvas edits and return to My Runbooks (this runbook pre-selected).
    exitToMyRunbooks();
  }, [editingVersion, exitToMyRunbooks]);

  const handleLoadTemplate = useCallback((template) => {
    if (nodes.length > 1) { // More than just the start node
      if (!window.confirm('Loading a template will replace your current workflow. Continue?')) {
        return;
      }
    }

    // Load the template workflow
    handleImportWorkflow(template.workflow);
  }, [nodes, handleImportWorkflow]);

  // Auto-format: reflow all step nodes into a clean, evenly-spaced vertical
  // layout at the current spacing — the same shape a runbook gets when it has no
  // saved positions. Lets existing runbooks (with old wide spacing) adopt the
  // new tighter layout in one click. Manually-grouped nodes are left untouched.
  const autoFormatLayout = useCallback(() => {
    const START_X = 250;
    const START_Y = 50;
    const STEP_SPACING = 84; // matches add-step spacing
    const INDENT = 48;       // matches forEach nesting indent (4 grid squares)

    // Topological (flow) order, following edges. Skip start + grouped nodes.
    const sortedSteps = [];
    const visited = new Set();
    const temp = new Set();
    const visit = (nodeId) => {
      if (temp.has(nodeId) || visited.has(nodeId)) return;
      temp.add(nodeId);
      edges.filter((e) => e.target === nodeId).forEach((e) => visit(e.source));
      temp.delete(nodeId);
      visited.add(nodeId);
      const node = nodes.find((n) => n.id === nodeId);
      if (node && node.id !== 'start' && node.type !== 'groupNode' && node.type !== 'noteNode' && !node.parentNode) {
        sortedSteps.push(node);
      }
    };
    // Seed from whatever 'start' is actually connected to (see the same fix in
    // generateWorkflowJson) so auto-format lays out the real first step first, regardless of
    // node array/creation order.
    const startEdge = edges.find((e) => e.source === 'start');
    if (startEdge) visit(startEdge.target);
    nodes.forEach((n) => {
      if (n.id !== 'start' && n.type !== 'groupNode' && n.type !== 'noteNode' && !n.parentNode) visit(n.id);
    });

    if (sortedSteps.length === 0) return;

    // Indent on forEach, dedent on endLoop. A forEach sits at the current level
    // and its body indents one level; the matching endLoop dedents first so it
    // lands back at the forEach's level.
    const posById = {};
    let depth = 0;
    sortedSteps.forEach((node, index) => {
      let nodeDepth = depth;
      if (node.data.stepType === 'endLoop') {
        depth = Math.max(0, depth - 1);
        nodeDepth = depth;
      }
      posById[node.id] = {
        x: START_X + nodeDepth * INDENT,
        y: START_Y + STEP_SPACING + index * STEP_SPACING,
      };
      if (node.data.stepType === 'forEach') {
        depth += 1;
      }
    });

    const firstStep = sortedSteps[0];
    const startPos = {
      x: posById[firstStep.id].x,
      y: posById[firstStep.id].y - STEP_SPACING,
    };

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === 'start') return { ...node, position: startPos };
        const p = posById[node.id];
        return p ? { ...node, position: p } : node;
      })
    );

    // Re-fit the view to the freshly laid-out graph.
    setTimeout(() => {
      reactFlowInstance.current?.fitView({ maxZoom: 1, padding: 0.2, duration: 300 });
    }, 60);
  }, [nodes, edges, setNodes]);

  // #2663/#2664: branch-aware layout (src/utils/runbookLayout.js) -- places If/Else branches
  // side by side (True right of False) and loop bodies as indented boxes, then routes long lines
  // around steps instead of through them, unlike Auto-format's single-column stack. Manually-
  // grouped nodes are left untouched, same as Auto-format -- generateWorkflowJson's step list
  // still includes them (it needs to, for connectivity through the group), the skip only happens
  // here at write-back time.
  //
  // The engine assumes one uniform box size for every step (it isn't a per-node-size API), so
  // this reads the real rendered width/height off the live canvas (React Flow measures these
  // automatically) and uses the largest of each as that uniform size -- real numbers instead of
  // the engine's built-in 300x70 guess, without the much larger rewrite true per-node sizing (or
  // modeling the actual bezier/handle-aware edge curves) would need. See ticket discussion: this
  // is the pragmatic pass, not a claim of pixel-perfect accuracy for every step's real geometry.
  const smartFormatLayout = useCallback(() => {
    if (nodes.length <= 1) return;

    const { steps } = generateWorkflowJson();
    if (!steps.length) return;

    const stepNumberByNodeId = new Map(nodes.map((n) => [n.id, n.data?.stepId || n.id]));
    const stepNumbers = new Set(steps.map((s) => s.stepNumber));
    const measuredNodes = nodes.filter(
      (n) => stepNumbers.has(stepNumberByNodeId.get(n.id)) && n.width && n.height
    );
    const sizeOverrides = measuredNodes.length
      ? { nodeW: Math.max(...measuredNodes.map((n) => n.width)), nodeH: Math.max(...measuredNodes.map((n) => n.height)) }
      : {};

    const { positions, routes } = formatRunbook(steps, sizeOverrides);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === 'start' || node.type === 'groupNode' || node.type === 'noteNode' || node.parentNode) {
          return node;
        }
        const pos = positions.get(stepNumberByNodeId.get(node.id));
        return pos ? { ...node, position: { x: pos[0], y: pos[1] } } : node;
      })
    );

    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.sourceHandle !== 'next' && edge.sourceHandle !== 'true' && edge.sourceHandle !== 'false') {
          return edge;
        }
        const sourceStepNumber = stepNumberByNodeId.get(edge.source);
        const pts = (routes.get(`${sourceStepNumber}|${edge.sourceHandle}`) || []).map(([x, y]) => ({
          x: Math.round(x),
          y: Math.round(y),
        }));
        return { ...edge, data: { ...edge.data, waypoints: pts } };
      })
    );

    setTimeout(() => {
      reactFlowInstance.current?.fitView({ maxZoom: 1, padding: 0.2, duration: 300 });
    }, 60);
  }, [nodes, generateWorkflowJson, setNodes, setEdges]);

  const createGroup = useCallback(() => {
    // Get all selected nodes (non-group, non-note nodes only — a note has no fixed step
    // dimensions, which the bounding-box math below assumes)
    const selectedNodes = nodes.filter(node => node.selected && node.type !== 'groupNode' && node.type !== 'noteNode');

    if (selectedNodes.length === 0) {
      alert('Please select at least one node to group');
      return;
    }

    const groupId = `RG${groupCounter}`;

    // Calculate bounding box for selected nodes with minimal padding
    const minX = Math.min(...selectedNodes.map(n => n.position.x)) - 20;
    const minY = Math.min(...selectedNodes.map(n => n.position.y)) - 50;
    const maxX = Math.max(...selectedNodes.map(n => n.position.x + 200)); // Node width
    const maxY = Math.max(...selectedNodes.map(n => n.position.y + 100)); // Node height

    // Create the group node
    const groupNode = {
      id: groupId,
      type: 'groupNode',
      position: { x: minX, y: minY },
      style: {
        width: maxX - minX + 40,
        height: maxY - minY + 30,
      },
      data: {
        label: `Group ${groupCounter}`,
      },
      zIndex: -1, // Put group behind other nodes
    };

    // Update selected nodes to be children of the group and add groupId property
    const updatedNodes = nodes.map(node => {
      if (selectedNodes.find(n => n.id === node.id)) {
        return {
          ...node,
          position: {
            x: node.position.x - minX,
            y: node.position.y - minY,
          },
          parentNode: groupId,
          extent: 'parent',
          zIndex: 1, // Ensure child nodes are above the group node for click events
          data: {
            ...node.data,
            groupId: groupId,
          },
        };
      }
      return node;
    });

    setNodes([...updatedNodes, groupNode]);
    setGroupCounter(prev => prev + 1);
  }, [nodes, groupCounter, setNodes]);

  const ungroupNodes = useCallback(() => {
    const selectedGroups = nodes.filter(node => node.selected && node.type === 'groupNode');

    if (selectedGroups.length === 0) {
      alert('Please select a group to ungroup');
      return;
    }

    const groupIds = selectedGroups.map(g => g.id);
    const parentGroup = selectedGroups[0];

    // Remove parent relationship from child nodes, restore absolute positions, and remove groupId
    const updatedNodes = nodes
      .filter(node => !groupIds.includes(node.id)) // Remove group nodes
      .map(node => {
        if (node.parentNode && groupIds.includes(node.parentNode)) {
          return {
            ...node,
            position: {
              x: node.position.x + parentGroup.position.x,
              y: node.position.y + parentGroup.position.y,
            },
            parentNode: undefined,
            extent: undefined,
            data: {
              ...node.data,
              groupId: undefined,
            },
          };
        }
        return node;
      });

    setNodes(updatedNodes);
  }, [nodes, setNodes]);

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
          tooltip: {
            borderRadius: "0.75rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
          tooltipContent: {
            padding: "20px",
          },
        }}
      />
      {!hideTitle && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-2" data-tour="workflow-title">
            <h4 className="mb-0">
              <BsDiagram3 className="mb-1" /> Workflow Builder
              {editingVersion && (
                <span className="ms-3">
                  <Badge bg="secondary" className="me-2">Runbook {editingVersion.runbookId}</Badge>
                  {editingVersion.runbookName && (
                    <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>{editingVersion.runbookName}</span>
                  )}
                </span>
              )}
            </h4>
          </div>
          <hr className="mt-2" />
        </>
      )}

      {editingVersion && (
        <Container fluid className="mb-3">
          <Row>
            <Col>
              <Card className="border-primary">
                <Card.Body className="py-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <Badge bg="primary" className="me-2">Version {editingVersion.versionNum}</Badge>
                      {editingVersion.description && (
                        <span className="text-muted ms-2">- {editingVersion.description}</span>
                      )}
                    </div>
                    <small className="text-muted">Remember to save your changes</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}

      {showImportStrippedNotice && (
        <Container fluid className="mb-3">
          <Row>
            <Col>
              <Alert variant="info" dismissible onClose={() => setShowImportStrippedNotice(false)}>
                <Alert.Heading className="h6">Variables were not imported</Alert.Heading>
                <p className="mb-0 small">
                  Variables are specific to each client and are never carried across on import. Add the variables for this client and map them to the relevant steps before saving.
                </p>
              </Alert>
            </Col>
          </Row>
        </Container>
      )}

      <Container fluid>
        {/* Runbook Connectors & Variables Bar */}
        <Row className="mb-3">
          <Col>
            <Card data-tour="connectors-bar">
              <Card.Body className="py-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                      <strong className="small">Connectors:</strong>
                      {runbookConnectors.length === 0 ? (
                        <span className="text-muted small">None</span>
                      ) : (
                        runbookConnectors.map((conn) => (
                          <OverlayTrigger
                            key={conn.connectorId}
                            trigger={['hover', 'focus']}
                            placement="bottom"
                            overlay={
                              <Popover style={{ minWidth: '480px', fontSize: '1rem' }}>
                                <Popover.Header as="h5" style={{ backgroundColor: '#cf7f00', color: 'white', fontSize: '1.1rem' }}>
                                  {conn.connectorNumber}
                                </Popover.Header>
                                <Popover.Body style={{ fontSize: '0.95rem' }}>
                                  <div className="mb-2"><strong>System:</strong> {conn.system}</div>
                                  <div><strong>Description:</strong> {conn.description || <span className="text-muted">None</span>}</div>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <Badge
                              // Drop the bg-primary utility when we have a connector color:
                              // Bootstrap's .bg-primary is !important (and the brand orange),
                              // so it would override the inline backgroundColor otherwise.
                              bg={
                                (connectorColors[conn.connectorId] || connectorColors[conn.connectorNumber])
                                  ? ''
                                  : 'primary'
                              }
                              className="d-flex align-items-center gap-1"
                              style={{
                                cursor: 'help',
                                backgroundColor:
                                  connectorColors[conn.connectorId] ||
                                  connectorColors[conn.connectorNumber] ||
                                  undefined,
                                color: '#fff',
                              }}
                            >
                              {conn.connectorNumber}
                            </Badge>
                          </OverlayTrigger>
                        ))
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <strong className="small">Variables:</strong>
                      {runbookVariables.length === 0 ? (
                        <span className="text-muted small">No variables included on runbook</span>
                      ) : (
                        runbookVariables.map((variable) => (
                          <OverlayTrigger
                            key={variable.variableId}
                            trigger={['hover', 'focus']}
                            placement="bottom"
                            overlay={
                              <Popover style={{ minWidth: '480px', fontSize: '1rem' }}>
                                <Popover.Header as="h5" style={{ backgroundColor: '#198754', color: 'white', fontSize: '1.1rem' }}>
                                  {variable.variableNumber}
                                </Popover.Header>
                                <Popover.Body style={{ fontSize: '0.95rem' }}>
                                  <div className="mb-2"><strong>Name:</strong> {variable.variableName || <span className="text-muted">None</span>}</div>
                                  <div><strong>Description:</strong> {variable.description || <span className="text-muted">None</span>}</div>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <Badge bg="success" className="d-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                              {variable.variableNumber}
                            </Badge>
                          </OverlayTrigger>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {editingVersion && !isReadOnly && (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openApplyModal(false)}
                          disabled={saving || nodes.length === 0}
                          title="Save the current canvas as a new version and make it the live one"
                        >
                          <FaSave className="me-1" /> {saving ? 'Applying...' : 'Apply'}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openApplyModal(true)}
                          disabled={saving || nodes.length === 0}
                        >
                          <FaCheckCircle className="me-1" /> Apply & Close
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleValidate}
                          disabled={saving}
                        >
                          <MdGppGood className="me-1" /> Validate
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => window.open(`/runbook/versions?runbookId=${editingVersion.runbookId}&runbookGuid=${editingVersion.runbookGuid}`, "_blank")}
                          disabled={saving}
                          title="View this runbook's versions"
                        >
                          <FaCodeBranch className="mb-1 me-2" /> View Versions
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleDiscardClick}
                          disabled={saving}
                        >
                          <FaTimes className="me-1" /> Discard
                        </Button>
                      </>
                    )}
                    {editingVersion && isReadOnly && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/runbook/versions?runbookId=${editingVersion.runbookId}&runbookGuid=${editingVersion.runbookGuid}`)}
                      >
                        <FaTimes className="me-1" /> Close
                      </Button>
                    )}
                    {/* Show templates when workflow is empty (only start node) */}
                    {nodes.length <= 1 && (
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-primary" size="sm" id="templates-dropdown">
                          <FaFileAlt className="me-1" /> Templates
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Header>Workflow Templates</Dropdown.Header>
                          {workflowTemplates.map((template, index) => (
                            <Dropdown.Item
                              key={index}
                              onClick={() => handleLoadTemplate(template)}
                            >
                              <div>
                                <strong>{template.metadata.name}</strong>
                                <div className="text-muted small">{template.metadata.description}</div>
                              </div>
                            </Dropdown.Item>
                          ))}
                          {workflowTemplates.length === 0 && (
                            <Dropdown.Item disabled>No templates available</Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-primary"
                        size="sm"
                        id="workflow-tools-dropdown"
                        data-tour="workflow-tools-btn"
                      >
                        <FaFileExport className="me-1" /> Workflow
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => setShowImportModal(true)}
                          disabled={isReadOnly}
                          title={isReadOnly ? "Cannot import in read-only mode" : "Import workflow from JSON"}
                        >
                          <FaPlus className="me-1" /> Import Workflow
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={handleExportWorkflow}
                          disabled={nodes.length === 0}
                        >
                          <FaFileExport className="me-1" /> Export Workflow
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setShowJsonPreview(true)}
                          disabled={nodes.length === 0}
                        >
                          <FaCode className="me-1" /> View JSON
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={autoFormatLayout}
                          disabled={nodes.length <= 1}
                          title="Re-arrange all steps into a clean, evenly-spaced layout"
                        >
                          <FaAlignLeft className="me-1" /> Auto-format
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={smartFormatLayout}
                          disabled={nodes.length <= 1}
                          title="Branch-aware layout: True/False side by side, loops as boxes, long lines routed around steps"
                        >
                          <FaCodeBranch className="me-1" /> Smart Format
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={createGroup}
                      title="Select multiple nodes and click to group them"
                    >
                      <FaObjectGroup className="me-1" /> Group
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={ungroupNodes}
                      title="Select a group and click to ungroup"
                    >
                      <FaObjectUngroup className="me-1" /> Ungroup
                    </Button>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-primary"
                        size="sm"
                        id="add-resource-dropdown"
                        data-tour="add-resource-btn"
                      >
                        <FaPlus className="me-1" /> Add
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setShowAddConnectorModal(true)}>
                          <FaPlus className="me-1" /> Add Connector
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowAddVariableModal(true)}>
                          <FaPlus className="me-1" /> Add Variable
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Read-Only Banner */}
        {isReadOnly && (
          <Alert variant="info" className="mb-3">
            <strong>Read-Only Mode:</strong> You are viewing this workflow. Editing is disabled.
          </Alert>
        )}

        <div ref={canvasRowRef} className="d-flex" style={{ flex: '1 1 auto', minHeight: 0 }}>
          {/* Left Sidebar - Step Palette */}
          {!isReadOnly && (
            paletteCollapsed ? (
              <div style={{ flexShrink: 0, marginRight: '8px' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setPaletteCollapsed(false)}
                  style={{ width: '28px', height: canvasHeight, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0 6px 6px 0', minHeight: '600px' }}
                  title="Show Step Palette"
                >
                  <FaChevronRight size={12} />
                </Button>
              </div>
            ) : (
              <div style={{ flexShrink: 0, width: '16.666%', paddingRight: '12px' }}>
                <div data-tour="step-palette" style={{ position: 'relative' }}>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setPaletteCollapsed(true)}
                    className="d-flex align-items-center gap-1 text-white"
                    style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1, padding: '4px 8px', fontSize: '0.75rem', textDecoration: 'none' }}
                    title="Collapse Step Palette"
                  >
                    <FaChevronLeft /> Hide
                  </Button>
                  <StepPalette onAddNode={addNewNode} />
                  <EdgeLegend />
                </div>
              </div>
            )
          )}


          {/* Main Canvas */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Card
              className={`workflow-canvas-card ${aiCanvasFlash ? 'ai-canvas-flash' : ''}`}
              data-tour="workflow-canvas"
              style={hideTitle ? { height: canvasHeight, minHeight: '600px' } : { flex: '1 1 auto', minHeight: 0 }}
            >
              <Card.Body className="p-0" style={hideTitle ? { height: '100%' } : { flex: '1 1 auto', minHeight: 0 }}>
                <div style={{ height: hideTitle ? '100%' : canvasHeight, minHeight: '600px', position: 'relative' }}>
                  <NodeDisplayContext.Provider value={nodeDisplayValue}>
                  <WorkflowEdgesContext.Provider value={edgesContextValue}>
                  <WorkflowNodesContext.Provider value={setNodes}>
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={isReadOnly ? undefined : onConnect}
                    onNodeClick={onNodeClick}
                    onPaneClick={onPaneClick}
                    onSelectionContextMenu={isReadOnly ? undefined : onSelectionContextMenu}
                    onEdgeContextMenu={isReadOnly ? undefined : onEdgeContextMenu}
                    onNodeContextMenu={isReadOnly ? undefined : onNodeContextMenu}
                    onPaneContextMenu={isReadOnly ? undefined : onPaneContextMenu}
                    onNodesDelete={isReadOnly ? undefined : onNodesDelete}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    nodesDraggable={!isReadOnly}
                    nodesConnectable={!isReadOnly}
                    elementsSelectable={true}
                    snapToGrid={snapToGrid}
                    snapGrid={[SNAP_GRID_SIZE, SNAP_GRID_SIZE]}
                    panOnDrag={dragMode === 'pan'}
                    selectionOnDrag={dragMode === 'select'}
                    selectionMode="partial"
                    fitView
                    fitViewOptions={{ maxZoom: 1, padding: 0.2 }}
                    minZoom={0.15}
                    attributionPosition="bottom-left"
                    deleteKeyCode="Delete"
                    onInit={(instance) => { reactFlowInstance.current = instance; }}
                    // Edges default to zIndex 0, same as nodes, so ties fall back to DOM order —
                    // which puts nodes on top, letting an edge visually disappear under any block
                    // it happens to pass behind. A flat default above the highest a node can reach
                    // (1000 from React Flow's own selected-node elevation, plus this app's own
                    // group/child node zIndex of -1/1) keeps every edge on top unconditionally.
                    defaultEdgeOptions={{ zIndex: 2000 }}
                  >
                    <Panel position="top-left">
                      <div className="btn-group" role="group" aria-label="Drag mode toggle">
                        <button
                          type="button"
                          className={`btn btn-sm ${dragMode === 'pan' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setDragMode('pan')}
                          title="Pan mode - drag to move canvas"
                        >
                          <FaHandPaper />
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${dragMode === 'select' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setDragMode('select')}
                          title="Select mode - drag to select multiple nodes"
                        >
                          <FaMousePointer />
                        </button>
                      </div>
                      <button
                        type="button"
                        className={`btn btn-sm ms-2 ${snapToGrid ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={toggleSnapToGrid}
                        title="Snap steps to a grid when dragging"
                      >
                        Snap to grid
                      </button>
                    </Panel>
                    <Panel position="top-right">
                      <div className="bg-white rounded shadow-sm border" style={{ width: '280px' }}>
                          <div className="p-2">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <FaSearch className="text-muted" size={12} />
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search by name or step ID..."
                                value={stepSearch}
                                onChange={(e) => setStepSearch(e.target.value)}
                              />
                              {stepSearch && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-muted p-0"
                                  onClick={() => setStepSearch('')}
                                >
                                  <FaTimes />
                                </button>
                              )}
                            </div>
                            {stepSearch.length > 0 && (
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {nodes
                                  .filter((n) => {
                                    const q = stepSearch.toLowerCase();
                                    const label = n.data.label?.toLowerCase() || '';
                                    const stepId = n.data.stepId?.toLowerCase() || '';
                                    return label.includes(q) || stepId.includes(q);
                                  })
                                  .map((n) => (
                                    <button
                                      key={n.id}
                                      className="btn btn-sm btn-light w-100 text-start mb-1"
                                      onClick={() => {
                                        if (reactFlowInstance.current) {
                                          reactFlowInstance.current.setCenter(
                                            n.position.x + 100,
                                            n.position.y + 30,
                                            { zoom: 1.2, duration: 500 }
                                          );
                                        }
                                        setSelectedNode(n);
                                        setStepSearch('');
                                      }}
                                    >
                                      <span>
                                        {n.data.stepId && <strong className="text-primary me-1">{n.data.stepId}</strong>}
                                        {n.data.label}
                                        <span className="text-muted ms-1">({n.data.stepType})</span>
                                      </span>
                                    </button>
                                  ))}
                                {nodes.filter((n) => { const q = stepSearch.toLowerCase(); return (n.data.label?.toLowerCase() || '').includes(q) || (n.data.stepId?.toLowerCase() || '').includes(q); }).length === 0 && (
                                  <small className="text-muted d-block text-center py-2">No matching steps</small>
                                )}
                              </div>
                            )}
                          </div>
                      </div>
                    </Panel>
                    <Controls />
                    <MiniMap
                      nodeStrokeColor={(n) => colorForNode(n.data, connectorColors)}
                      nodeColor={(n) => colorForNode(n.data, connectorColors)}
                      nodeBorderRadius={2}
                      pannable
                      zoomable
                    />
                    <Background variant={BackgroundVariant.Dots} gap={SNAP_GRID_SIZE} size={1} />
                  </ReactFlow>
                  </WorkflowNodesContext.Provider>
                  </WorkflowEdgesContext.Provider>
                  </NodeDisplayContext.Provider>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* CoryBot — draggable floating window over the builder (fixed-position, outside
              the canvas so it doesn't consume canvas space). #2666: canUseCoryBot is a per-user
              grant a SuperUser/ProductTeam admin can hand out (Edit User screen) to let someone
              else in without making them SuperUser/ProductTeam -- aiBuilderEnabled is already off
              in QA/production, so this only has any effect in dev-like environments today. */}
          {config.aiBuilderEnabled && !isReadOnly &&
            (props.user?.isSuperUser || props.user?.isProductTeam || props.user?.canUseCoryBot) && (
            <KbBuilderBubble
              clientId={props.user?.clientId}
              clientGuid={props.user?.clientGuid}
              getCurrentRunbookJson={generateWorkflowJson}
              onHydrate={(json) => {
                handleImportWorkflow(json);
                triggerCanvasFlash();
              }}
            />
          )}

          {/* Right Sidebar - Step Configuration */}
          {selectedNode && (
            <div style={{ flexShrink: 0, width: `${trayWidth}px`, paddingLeft: '12px', position: 'relative' }}>
              <div
                className="tray-resize-handle"
                onMouseDown={handleTrayResizeMouseDown}
                title="Drag to resize"
              />
              <StepConfigPanel
                node={selectedNode}
                onUpdate={updateNodeData}
                onClose={(isCancel) => {
                  // Only save pending config if this is NOT a cancel operation
                  if (!isCancel && pendingNodeConfig && !isReadOnly) {
                    updateNodeData(selectedNode.id, pendingNodeConfig);
                  }
                  setSelectedNode(null);
                }}
                onConfigChange={setPendingNodeConfig}
                connectors={runbookConnectors}
                runbookVariables={runbookVariables}
                clientVariables={clientVariableReducer.paginatedResult?.data || []}
                availableRunbooks={availableRunbooks}
                allNodes={nodes}
                edges={edges}
                readOnly={isReadOnly}
                runbookContext={{
                  runbookId: editingVersion?.runbookId || '',
                  runbookGuid: editingVersion?.runbookGuid || '',
                  clientId: props.user?.clientId || '',
                  clientGuid: props.user?.clientGuid || '',
                }}
                onScaffoldFromPreview={handleScaffoldFromPreview}
                onCopyFieldMappings={(targetNodeId, fieldMappings) => {
                  // Copy field mappings to target node, merging with existing mappings
                  setNodes(nds => nds.map(n => {
                    if (n.id === targetNodeId) {
                      const existingMappings = n.data.fieldMappings || [];
                      // Add new mappings, avoiding duplicates by name
                      const newMappings = fieldMappings.filter(
                        fm => !existingMappings.some(em => em.name === fm.name)
                      );
                      return {
                        ...n,
                        data: {
                          ...n.data,
                          fieldMappings: [...existingMappings, ...newMappings]
                        }
                      };
                    }
                    return n;
                  }));
                }}
              />
            </div>
          )}
        </div>
      </Container>

      {/* Connector Management Modal */}
      <ConnectorModal
        show={showAddConnectorModal}
        onHide={() => setShowAddConnectorModal(false)}
        availableConnectors={clientConnectorReducer.paginatedResult?.data || []}
        runbookConnectors={runbookConnectors}
        onAddConnector={(connector) => {
          if (!runbookConnectors.find(c => c.connectorId === connector.connectorId)) {
            setRunbookConnectors([...runbookConnectors, connector]);
          }
        }}
        onRemoveConnector={(connectorId) => {
          setRunbookConnectors(runbookConnectors.filter(c => c.connectorId !== connectorId));
        }}
      />

      {/* Variable Management Modal */}
      <VariableModal
        show={showAddVariableModal}
        onHide={() => setShowAddVariableModal(false)}
        availableVariables={clientVariableReducer.paginatedResult?.data || []}
        runbookVariables={runbookVariables}
        onAddVariable={(variable) => {
          if (!runbookVariables.find(v => v.variableId === variable.variableId)) {
            setRunbookVariables([...runbookVariables, variable]);
          }
        }}
        onRemoveVariable={(variableId) => {
          setRunbookVariables(runbookVariables.filter(v => v.variableId !== variableId));
        }}
      />

      {/* Import Workflow Modal */}
      <ImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        availableConnectors={clientConnectorReducer.paginatedResult?.data || []}
        onImport={handleImportWorkflow}
      />

      {/* Path Selection Modal for If/Else steps */}
      <PathSelectionModal
        show={showPathSelectionModal}
        onSelect={handlePathSelect}
        onCancel={() => {
          setPendingStepContext(null);
          setShowPathSelectionModal(false);
        }}
      />

      {/* Step type picker for "Add Step Before"/"Add Step After" (step right-click menu) */}
      <StepTypePickerModal
        show={!!insertStepPicker}
        title={insertStepPicker?.mode === 'before' ? 'Add Step Before' : 'Add Step After'}
        onSelect={handleInsertStepTypeSelected}
        onCancel={() => setInsertStepPicker(null)}
      />

      {/* JSON Preview Offcanvas */}
      <Offcanvas
        show={showJsonPreview}
        onHide={() => setShowJsonPreview(false)}
        placement="end"
        style={{ width: '50%' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Workflow JSON Preview</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <pre style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '0.5rem',
            fontSize: '0.85rem',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 100px)'
          }}>
            {JSON.stringify(generateWorkflowJson(), null, 2)}
          </pre>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Validation Results Modal */}
      <Modal show={showApplyInvalidModal} onHide={dismissApplyInvalid} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <MdGppBad className="mb-1 me-2 text-danger" />
            Applied — not valid yet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Your changes were applied as the latest version, but it did not pass validation — so{' '}
            <strong>it will not run</strong> until the issues are fixed:
          </p>
          <div className="text-danger">{applyInvalidMessage}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissApplyInvalid}>OK</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showApplyModal}
        onHide={() => setShowApplyModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingApplyClose ? 'Apply & Close' : 'Apply'} — new version
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label htmlFor="apply-version-description" className="form-label">
            Version description
          </label>
          <input
            id="apply-version-description"
            type="text"
            className="form-control"
            placeholder="Describe what changed in this version"
            value={applyDescription}
            onChange={(e) => setApplyDescription(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') confirmApply(); }}
          />
          <div className="form-text">
            Applying saves the current canvas as a new version and makes it the live one.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApplyModal(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmApply} disabled={saving}>
            <FaSave className="me-1" /> {saving ? 'Applying...' : (pendingApplyClose ? 'Apply & Close' : 'Apply')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showValidateModal}
        onHide={() => setShowValidateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Validate Workflow - {editingVersion?.runbookId} (Version {editingVersion?.versionNum})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {runbookReducer.loadingValidate && (
            <>
              Validating workflow...
              <Spinner
                className="float-end"
                size="sm"
                animation="border"
                variant="primary"
              />
            </>
          )}

          {!runbookReducer.loadingValidate && (
            <>
              {runbookReducer.validateResponse && (
                <>
                  {!runbookReducer.validateResponse.hasErrors ? (
                    <div className="text-success">
                      <MdGppGood className="me-2 mb-1" />
                      Validation successful!
                    </div>
                  ) : (
                    <div className="text-danger">
                      <MdGppBad className="me-2 mb-1" />
                      {runbookReducer.validateResponse.message ||
                        "Validation failed. Please check the details."}
                    </div>
                  )}
                </>
              )}

              {runbookReducer.validateError && (
                <div className="text-danger">
                  <MdGppBad className="me-2 mb-1" />
                  Validation failed with an error. Please try again.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValidateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Discard Confirmation Modal */}
      <Modal
        show={showDiscardModal}
        onHide={() => setShowDiscardModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaTimes className="me-2 mb-1" />
            Discard Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>Are you sure you want to discard all changes?</p>
          <div className="bg-light p-3 rounded">
            <div className="mb-2">
              <strong>Runbook:</strong> {editingVersion?.runbookId}
            </div>
            <div className="mb-0">
              <strong>Version:</strong> {editingVersion?.versionNum}
            </div>
          </div>
          <p className="mt-3 mb-0">
            All unsaved changes to this workflow will be permanently lost.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDiscardModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDiscard}>
            <FaTimes className="me-1" /> Discard Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Save Success Toast */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showSaveToast}
          onClose={() => setShowSaveToast(false)}
          delay={3000}
          autohide
          bg={saveSuccess ? 'success' : 'danger'}
        >
          <Toast.Header closeButton={true}>
            <strong className="me-auto">
              {saveSuccess ? (
                <>
                  <FaCheckCircle className="me-2 mb-1" />
                  Success
                </>
              ) : (
                <>
                  <FaTimes className="me-2 mb-1" />
                  Error
                </>
              )}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {saveSuccess ? (
              <>
                Version <strong>{editingVersion?.runbookId} v{editingVersion?.versionNum}</strong> saved successfully!
              </>
            ) : (
              'Failed to save version. Please try again.'
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {contextMenu &&
        createPortal(
          <>
            {/* Portaled straight to document.body rather than rendered in place: when this
                component is embedded in a DesktopWindow (the workspace "window" host), the
                window itself is positioned via a CSS transform (translate3d, for drag
                performance) — and a transform on ANY ancestor makes descendant
                position:fixed elements position relative to THAT ancestor instead of the real
                viewport. The menu would then drift from the cursor by however far the window
                had been dragged. Escaping to body sidesteps that entirely. */}
            {/* Full-screen transparent backdrop closes the menu on any outside click — simpler
                and more reliable than trying to detect "click outside the menu" via refs. */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 3000 }}
              onClick={closeMenu}
              onContextMenu={(e) => { e.preventDefault(); closeMenu(); }}
            />
            <div
              className="workflow-selection-menu"
              style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 3001 }}
            >
              {contextMenu.title && <div className="workflow-selection-menu-title">{contextMenu.title}</div>}
              {contextMenu.items.map((item) => (
                <button key={item.key} type="button" onClick={item.onClick}>
                  {item.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
});

export default WorkflowBuilder;


